import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import Card from '../components/UI/Card';
import Table from '../components/UI/Table';
import { useAuth } from '../contexts/AuthContext';
import AccessDenied from '../components/AccessDenied';
import { supabase, safeSupabaseQuery } from '../services/supabaseClient';
import { formatDateDDMMYYYY, formatDateTimeDDMMYYYY, formatDateTimeFullDDMMYYYY, convertirDDMMYYYYaISO, convertirISOaDDMMYYYY } from '../utils/dateFormatter';
import jsPDF from 'jspdf';
// Servicio de API para gastos
import { getGastos, createGastoWithOfflineFallback, type Gasto } from '../services/mockApi';
import { safeFetch } from '../utils/supabaseHelpers';
// Backend API para profiles
import { getProfilesFromBackend, updateAlertaEstado } from '../services/backendApi';
// Store offline
import { offlineStore } from '../services/offlineStore';
import type { OfflineExpense } from '../services/offlineQueue';
// 🧠 Servicio de Analítica Inteligente
import { 
  detectarTodasAnomalias, 
  generarPrediccion, 
  calcularDesviacionEstandar,
  type Anomalia 
} from '../services/cajaChicaAnalytics';
// Componentes de gráficos
import PieChartComponent from '../components/Charts/PieChart';
// Componente de auditoría
import AuditLogViewer from '../components/caja-chica/AuditLogViewer';
// Componente de detalle de gasto
import GastoDetailModal from '../components/caja-chica/GastoDetailModal';
// Componente de carga de imagen con OCR
import ImageUploadWithOCR from '../components/caja-chica/ImageUploadWithOCR';
import { OCRResult } from '../services/ocrService';
import { 
  Plus, 
  Download, 
  AlertTriangle, 
  TrendingUp, 
  DollarSign,
  User,
  FileText,
  CheckCircle,
  XCircle,
  RefreshCw,
  Shield,
  Clock,
  BarChart3,
  PieChart,
  Activity,
  ChevronRight,
  Target,
  HelpCircle,
  Settings,
  AlertCircle,
  CheckCircle2,
  History,
  Database,
  ShieldCheck,
  Brain,
  TrendingUp as TrendingUpIcon,
  BarChart4,
  LineChart,
  FileSpreadsheet,
  Moon,
  Sun,
  Bell,
  CheckCircle as CheckCircleIcon,
  XCircle as XCircleIcon,
  Info,
  Loader2,
  Server,
  Cpu,
  ChevronUp,
  ChevronDown,
  Calendar
} from 'lucide-react';
import { LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';

// Los gastos se cargan desde Supabase, no hay datos mock por defecto

const categorias = [
  'Todas',
  'Alojamiento',
  'Alimentación',
  'Combustible',
  'Viático',
  'Traslado',
  'Mantención de vehículos',
  'Materiales e insumos',
  'Bebestibles'
];

// Función para obtener el rol del usuario para visualización
// Ya no usamos mockUserRole, usamos el rol real del usuario

// Función para obtener el mes actual en español
const obtenerMesActual = (): 'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre' => {
  const meses: Array<'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre'> = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
  ];
  const mesActual = new Date().getMonth(); // 0-11
  return meses[mesActual];
};

// Mock data para auditoría
const mockAuditoria = [
  {
    id: 1,
    timestamp: '2025-01-15T14:30:00',
    usuario: 'Carlos Mendoza',
    accion: 'Creación',
    resultado: 'OK',
    detalles: 'Gasto de combustible registrado'
  },
  {
    id: 2,
    timestamp: '2025-01-15T14:35:00',
    usuario: 'Ana Garcia',
    accion: 'Aprobación',
    resultado: 'OK',
    detalles: 'Gasto aprobado automáticamente'
  },
  {
    id: 3,
    timestamp: '2025-01-15T14:40:00',
    usuario: 'Sistema',
    accion: 'Aprobación',
    resultado: 'OK',
    detalles: 'Gastos aprobados correctamente'
  }
];


// Componente helper para el botón Ver
const BotonVer: React.FC<{ anomalia: Anomalia; handleVerDetalleAlerta: (anomalia: Anomalia) => void }> = ({ anomalia, handleVerDetalleAlerta }) => {
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const handleClickRef = useRef<((anomalia: Anomalia) => void) | null>(null);

  // Actualizar el ref con la función cuando cambie
  useEffect(() => {
    handleClickRef.current = handleVerDetalleAlerta;
  }, [handleVerDetalleAlerta]);

  // Callback ref para asegurar que el ref se asigne inmediatamente y agregar listeners
  const setButtonRef = useCallback((button: HTMLButtonElement | null) => {
    // Asignar el nuevo ref
    buttonRef.current = button;
    
    if (button) {
      // Ref asignado correctamente (sin log)

      // Agregar listeners DOM directos INMEDIATAMENTE cuando el ref esté disponible
      const handleDirectClickCapture = (e: MouseEvent) => {
        // EJECUTAR LA ACCIÓN directamente
        if (!button.disabled && handleClickRef.current) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          (e as any)._handledByDOM = true;
          handleClickRef.current(anomalia);
        }
      };

      const handleMouseDown = (e: MouseEvent) => {
        // Sin log - solo para debugging si es necesario
      };

      button.addEventListener('click', handleDirectClickCapture, true);
      button.addEventListener('click', handleDirectClickCapture, false); // También en fase de burbuja
      button.addEventListener('mousedown', handleMouseDown, true);

      // Listeners agregados correctamente (sin log)

      // Guardar referencias para cleanup
      (button as any)._clickCapture = handleDirectClickCapture;
      (button as any)._mousedown = handleMouseDown;
    }
  }, [anomalia]);

  // Cleanup de listeners cuando el componente se desmonte
  useEffect(() => {
    return () => {
      if (buttonRef.current) {
        const button = buttonRef.current;
        if ((button as any)._clickCapture) {
          button.removeEventListener('click', (button as any)._clickCapture, true);
        }
        if ((button as any)._mousedown) {
          button.removeEventListener('mousedown', (button as any)._mousedown, true);
        }
      }
    };
  }, []);

  return (
    <button
      ref={setButtonRef}
      type="button"
      id={`boton-ver-${anomalia.id}`}
      onClick={(e) => {
        // Listener DOM maneja el click, este es solo respaldo
        if (!(e as any)._handledByDOM) {
          handleVerDetalleAlerta(anomalia);
        }
      }}
      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors whitespace-nowrap flex-shrink-0"
      style={{ 
        position: 'relative', 
        zIndex: 10004,
        pointerEvents: 'auto',
        cursor: 'pointer',
        display: 'block',
        visibility: 'visible',
        opacity: 1
      }}
    >
      Ver
    </button>
  );
};

// Componente helper para el dropdown de estado
interface DropdownEstadoProps {
  anomalia: Anomalia;
  dropdownAbierto: string | null;
  setDropdownAbierto: (value: string | null) => void;
  dropdownAbiertoRef: React.MutableRefObject<string | null>;
  actualizandoEstado: string | null;
  handleActualizarEstadoAlerta: (anomaliaId: string, gastoId: string, nuevoEstado: 'pendiente' | 'aprobado' | 'rechazado') => Promise<void>;
}

  const DropdownEstado: React.FC<DropdownEstadoProps> = ({
    anomalia,
    dropdownAbierto,
    setDropdownAbierto,
    dropdownAbiertoRef,
    actualizandoEstado,
    handleActualizarEstadoAlerta
  }) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement>(null);
    const justOpenedRef = useRef(false);
    const [menuPosition, setMenuPosition] = useState<{ top: number; left: number; width: number } | null>(null);
    const claveUnica = `${anomalia.tipo}_${anomalia.gasto.id}`;
    const isOpen = dropdownAbierto === claveUnica;

    // Ref para almacenar la función de toggle (se actualizará después)
    const handleToggleRef = useRef<((e?: React.MouseEvent) => void) | null>(null);

    // Callback ref para asegurar que el ref se asigne inmediatamente y agregar listeners
    const setButtonRef = useCallback((button: HTMLButtonElement | null) => {
      // Limpiar listeners anteriores si existe un botón previo
      if (buttonRef.current && buttonRef.current !== button) {
        // Los listeners se limpian en el useEffect cleanup
      }
      
      // Asignar el nuevo ref
      buttonRef.current = button;
      
      if (button) {
        // Ref asignado correctamente (sin log)

        // Agregar listeners DOM directos INMEDIATAMENTE cuando el ref esté disponible
        const handleDirectClickCapture = (e: MouseEvent) => {
          // EJECUTAR LA ACCIÓN directamente
          if (!button.disabled && handleToggleRef.current) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            handleToggleRef.current();
          }
        };

        const handleBubbleClick = (e: MouseEvent) => {
          // Sin log - solo para capturar en fase de burbuja
        };

        const handleMouseDown = (e: MouseEvent) => {
          // Sin log - solo para debugging si es necesario
        };

        const handleMouseUp = (e: MouseEvent) => {
          // Sin log - solo para debugging si es necesario
        };

        button.addEventListener('click', handleDirectClickCapture, true);
        button.addEventListener('click', handleBubbleClick, false);
        button.addEventListener('mousedown', handleMouseDown, true);
        button.addEventListener('mouseup', handleMouseUp, true);

        // Listeners agregados correctamente (sin log)

        // Guardar referencias para cleanup
        (button as any)._clickCapture = handleDirectClickCapture;
        (button as any)._clickBubble = handleBubbleClick;
        (button as any)._mousedown = handleMouseDown;
        (button as any)._mouseup = handleMouseUp;
      }
    }, []);

    // Cleanup de listeners cuando el componente se desmonte o el botón cambie
    useEffect(() => {
      return () => {
        if (buttonRef.current) {
          const button = buttonRef.current;
          if ((button as any)._clickCapture) {
            button.removeEventListener('click', (button as any)._clickCapture, true);
          }
          if ((button as any)._clickBubble) {
            button.removeEventListener('click', (button as any)._clickBubble, false);
          }
          if ((button as any)._mousedown) {
            button.removeEventListener('mousedown', (button as any)._mousedown, true);
          }
          if ((button as any)._mouseup) {
            button.removeEventListener('mouseup', (button as any)._mouseup, true);
          }
        }
      };
    }, []);

    // Sin logs de renderizado

    // Listener global removido para evitar logs excesivos
    // Descomentar solo si necesitas debugging específico
    /*
    useEffect(() => {
      const handleGlobalClick = (e: MouseEvent) => {
        // Código de debugging aquí
      };
      window.addEventListener('click', handleGlobalClick, true);
      return () => {
        window.removeEventListener('click', handleGlobalClick, true);
      };
    }, []);
    */

    // Calcular posición del menú cuando se abre - UN SOLO RAF
    useEffect(() => {
      if (isOpen && buttonRef.current) {
        requestAnimationFrame(() => {
          if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setMenuPosition({
              top: rect.bottom + window.scrollY + 4,
              left: rect.left + window.scrollX,
              width: rect.width
            });
          }
        });
      } else {
        setMenuPosition(null);
      }
    }, [isOpen]);

    // Listener de click global - CON DELAY PARA EVITAR CAPTURAR EL CLICK QUE ABRE
    useEffect(() => {
      if (!isOpen) {
        return;
      }

      const handleClickOutside = (event: MouseEvent) => {
        // Ignorar si acabamos de abrir el dropdown
        if (justOpenedRef.current) {
          return;
        }

        const target = event.target as Node;
        
        // Verificar si el click fue dentro del dropdown, menú o botón Ver
        const clickedOnButton = buttonRef.current?.contains(target);
        const clickedOnMenu = menuRef.current?.contains(target);
        
        // Verificar si el click fue en el botón "Ver" (buscar por ID que empiece con "boton-ver-")
        const clickedElement = target as HTMLElement;
        const clickedOnVerButton = clickedElement.id?.startsWith('boton-ver-') || 
                                   clickedElement.closest('[id^="boton-ver-"]') !== null;
        
        if (clickedOnButton || clickedOnMenu || clickedOnVerButton) {
          return;
        }
        
        // Click fuera del dropdown, cerrar
        dropdownAbiertoRef.current = null;
        setDropdownAbierto(null);
      };

      // Agregar listener con un delay para evitar capturar el click que abre el dropdown
      const timeoutId = setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('click', handleClickOutside);
      };
    }, [isOpen]); // Solo depende de isOpen, no de las funciones que pueden cambiar

    const handleClose = useCallback(() => {
      dropdownAbiertoRef.current = null;
      setDropdownAbierto(null);
    }, [setDropdownAbierto, dropdownAbiertoRef]);

    const handleToggle = useCallback((e?: React.MouseEvent) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      
      const nuevoEstado = isOpen ? null : claveUnica;
      
      // Si estamos abriendo, activar justOpenedRef ANTES de cambiar el estado
        if (!isOpen) {
          justOpenedRef.current = true;
          dropdownAbiertoRef.current = nuevoEstado;
          setDropdownAbierto(nuevoEstado);
          setTimeout(() => {
            justOpenedRef.current = false;
          }, 300);
        } else {
          dropdownAbiertoRef.current = nuevoEstado;
          setDropdownAbierto(nuevoEstado);
        }
    }, [isOpen, claveUnica, setDropdownAbierto, dropdownAbiertoRef]);

    // Actualizar el ref con la función de toggle cuando cambie
    useEffect(() => {
      handleToggleRef.current = handleToggle;
    }, [handleToggle]);

    return (
      <>
        <div 
          ref={dropdownRef}
          className="relative" 
          id={`dropdown-estado-${anomalia.tipo}-${anomalia.gasto.id}`}
          style={{ position: 'relative', zIndex: 100 }}
        >
          <button
            ref={setButtonRef}
            type="button"
            id={`dropdown-button-${claveUnica}`}
            onClick={(e) => {
              // Listener DOM maneja el click, este es solo respaldo
              handleToggle(e);
            }}
            disabled={actualizandoEstado === claveUnica}
            className="appearance-none bg-white border border-gray-300 rounded-lg px-3 py-1.5 pr-8 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px] w-full sm:w-auto cursor-pointer flex items-center justify-between relative"
            style={{ 
              pointerEvents: 'auto', 
              position: 'relative', 
              zIndex: 10003,
              display: 'block',
              visibility: 'visible',
              opacity: 1
            }}
          >
            <span className="flex-1 text-left">{anomalia.estado.charAt(0).toUpperCase() + anomalia.estado.slice(1)}</span>
            <ChevronDown className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform pointer-events-none ${isOpen ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Overlay de loading - pointer-events-none para no bloquear */}
          {actualizandoEstado === claveUnica && (
            <div 
              className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 rounded-lg pointer-events-none z-50"
            >
              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
          )}
        </div>

        {/* Menú renderizado con Portal - RENDERIZAR INMEDIATAMENTE CUANDO isOpen ES TRUE */}
        {isOpen && createPortal(
          <div 
            ref={menuRef}
            className="fixed bg-white border border-gray-300 rounded-lg shadow-xl"
            style={{
              top: menuPosition ? `${menuPosition.top}px` : '0px',
              left: menuPosition ? `${menuPosition.left}px` : '0px',
              width: menuPosition ? `${menuPosition.width}px` : '120px',
              minWidth: '120px',
              display: 'block',
              visibility: 'visible',
              opacity: 1,
              pointerEvents: 'auto',
              zIndex: 10010
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              e.stopPropagation();
            }}
          >
            {(['pendiente', 'aprobado', 'rechazado'] as const).map((estado) => (
              <button
                key={estado}
                type="button"
                onClick={async (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  // Cerrar el dropdown inmediatamente para mejor UX
                  handleClose();
                  
                  // Solo actualizar si el estado es diferente
                  if (estado !== anomalia.estado) {
                    console.log(`🔄 [DropdownEstado] Cambiando estado de ${anomalia.estado} a ${estado} para anomalía ${anomalia.id}`);
                    try {
                      await handleActualizarEstadoAlerta(
                        anomalia.id,
                        String(anomalia.gasto.id),
                        estado
                      );
                      console.log(`✅ [DropdownEstado] Estado actualizado exitosamente a ${estado}`);
                    } catch (error) {
                      console.error(`❌ [DropdownEstado] Error al actualizar estado:`, error);
                    }
                  } else {
                    console.log(`ℹ️ [DropdownEstado] Estado ya es ${estado}, no se actualiza`);
                  }
                }}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg transition-colors cursor-pointer ${
                  estado === anomalia.estado 
                    ? 'bg-blue-50 text-blue-700 font-medium' 
                    : 'text-gray-700'
                }`}
                style={{ 
                  pointerEvents: 'auto',
                  zIndex: 10011
                }}
              >
                {estado.charAt(0).toUpperCase() + estado.slice(1)}
              </button>
            ))}
          </div>,
          document.body
        )}
      </>
    );
  };

// Listener global silencioso - solo para debugging si es necesario
// Descomentar si necesitas debuggear clicks
/*
if (typeof window !== 'undefined') {
  const handleGlobalClickDebug = (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const isAlertButton = target.id?.startsWith('dropdown-button-') || 
                         target.id?.startsWith('boton-ver-') ||
                         target.closest('[id^="dropdown-button-"]') !== null ||
                         target.closest('[id^="boton-ver-"]') !== null;
    
    if (isAlertButton) {
      const button = target.tagName === 'BUTTON' ? target : target.closest('button') as HTMLElement;
      const elementAtPoint = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement;
      
      if (target !== elementAtPoint) {
        console.warn('⚠️ OVERLAY BLOQUEANDO:', button.id, elementAtPoint?.id);
      }
    }
  };
  window.addEventListener('click', handleGlobalClickDebug, true);
}
*/

export default function CajaChica() {
  const { hasPermission, user, isAdmin } = useAuth();
  
  // Obtener el rol del usuario para visualización y permisos
  const userRole = user?.rol || 'Usuario';
  const isAdministrador = isAdmin || userRole === 'Administrador';
  // Los usuarios que no son administradores ni contadoras pueden crear gastos
  const puedeCrearGastos = !isAdministrador && userRole !== 'Contadora';
  
  // Mapear el rol para visualización (mostrar "Trabajador" para usuarios normales)
  const rolParaMostrar = userRole === 'Usuario' ? 'Trabajador' : userRole;
  
  const [gastos, setGastos] = useState<any[]>([]);
  const [pendingExpenses, setPendingExpenses] = useState<OfflineExpense[]>([]);
  const [periodoFiltro, setPeriodoFiltro] = useState<'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre' | 'trimestre' | 'año' | 'personalizado'>(obtenerMesActual());
  
  const [fechaInicioPersonalizada, setFechaInicioPersonalizada] = useState<string>('');
  const [fechaFinPersonalizada, setFechaFinPersonalizada] = useState<string>('');
  const [filtros, setFiltros] = useState({
    fechaInicio: '',
    fechaFin: '',
    trabajador: '',
    categoria: 'Todas',
    montoMin: '',
    montoMax: ''
  });
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarAuditoria, setMostrarAuditoria] = useState(false);
  const [modoAuditoria, setModoAuditoria] = useState(false);
  const [mostrarDrawer, setMostrarDrawer] = useState(false);
  const [gastoSeleccionado, setGastoSeleccionado] = useState<Gasto | null>(null);
  const [modoOscuro, setModoOscuro] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [toast, setToast] = useState({ mostrar: false, mensaje: '', tipo: 'success' });
  const [trabajadores, setTrabajadores] = useState<Array<{ id: string; nombre: string; email: string }>>([]);
  const [cargandoTrabajadores, setCargandoTrabajadores] = useState(false);
  const [mostrarAlertas, setMostrarAlertas] = useState(false);
  const [mostrarAnalitica, setMostrarAnalitica] = useState(false);
  const [mostrarAnalisisGastoMes, setMostrarAnalisisGastoMes] = useState(false);
  const [mostrarDetalleTrabajadores, setMostrarDetalleTrabajadores] = useState(false);
  const [auditoriaReal, setAuditoriaReal] = useState<Array<{
    id: string;
    usuario: string;
    accion: string;
    fecha: string;
    detalle: string;
    resultado?: string;
  }>>([]);
  const [cargandoAuditoria, setCargandoAuditoria] = useState(false);
  const [nuevoGasto, setNuevoGasto] = useState({
    fecha: '',
    trabajador: '',
    descripcion: '',
    monto: '',
    categoria: '',
    documento: null,
    monto_ocr: undefined as number | undefined // ✅ NUEVO: Campo para monto detectado por OCR
  });

  // Función para formatear número con puntos como separadores de miles
  const formatearMonto = (valor: string): string => {
    // Remover todo lo que no sea número
    const soloNumeros = valor.replace(/\D/g, '');
    if (!soloNumeros) return '';
    // Formatear con puntos cada 3 dígitos
    return soloNumeros.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  // Función para convertir monto formateado a número
  const desformatearMonto = (valor: string): number => {
    return parseInt(valor.replace(/\./g, '')) || 0;
  };
  const [imagenBoleta, setImagenBoleta] = useState<File | null>(null);
  const [anomalias, setAnomalias] = useState<Anomalia[]>([]);
  const [prediccion, setPrediccion] = useState<any>(null);
  const [gastoDuplicadoDetectado, setGastoDuplicadoDetectado] = useState(false);
  
  // ✅ NUEVO: Referencia para evitar bucles infinitos al recargar gastos después de actualizar estados
  const recargandoPorActualizacionEstado = useRef(false);
  // Estados para modal de detalle de alerta
  const [gastoModalAbierto, setGastoModalAbierto] = useState(false);
  const [gastoSeleccionadoModal, setGastoSeleccionadoModal] = useState<Gasto | null>(null);
  const [actualizandoEstado, setActualizandoEstado] = useState<string | null>(null);
  const [dropdownAbierto, setDropdownAbierto] = useState<string | null>(null);
  const dropdownAbiertoRef = useRef<string | null>(null);
  
  // ✅ OPTIMIZADO: Estado para controlar cuando mostrar la UI
  const [ready, setReady] = useState(false);

  // ✅ OPTIMIZADO: Mostrar UI inmediatamente
  useEffect(() => {
    setReady(true); // Mostrar UI de inmediato
  }, []);

  // ✅ OPTIMIZADO: Cargar trabajadores después de mostrar UI (carga diferida)
  useEffect(() => {
    if (!ready) return; // Esperar a que la UI esté lista
    
    // Cargar trabajadores desde la base de datos (solo rol "Trabajador", excluir Administradores)
    const cargarTrabajadores = async () => {
      setCargandoTrabajadores(true);
      try {
        // ✅ ELIMINADO: Intentar RPC primero - usar directamente fallback
        // El RPC no funciona, usar SELECT directo a profiles
        await cargarTrabajadoresFallback();
      } catch (error) {
        // Intentar fallback en caso de error
        await cargarTrabajadoresFallback();
      } finally {
        setCargandoTrabajadores(false);
      }
    };

    // Función fallback para cargar trabajadores desde la tabla profiles
    const cargarTrabajadoresFallback = async () => {
      try {
        let profilesData: any = null;
        let profilesError: any = null;

        // ✅ NUEVO: Intentar primero con backend como puente
        const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
        if (BACKEND_URL) {
          try {
            const backendResult = await getProfilesFromBackend();

            if (backendResult.success && Array.isArray(backendResult.data)) {
              profilesData = backendResult.data;
              profilesError = null;
            } else {
              console.warn('⚠️ [CajaChica] Backend respondió pero sin datos válidos:', {
                success: backendResult.success,
                dataIsArray: Array.isArray(backendResult.data),
                data: backendResult.data
              });
            }
          } catch (backendError: any) {
            console.warn('⚠️ [CajaChica] Backend falló, intentando Supabase directo:', backendError?.message);
            console.error('⚠️ [CajaChica] Error completo del backend:', backendError);
            // Continuar con Supabase directo como fallback
          }
        } else {
          console.log('ℹ️ [CajaChica] Backend URL no configurada, usando Supabase directo');
        }

        // ✅ FALLBACK: Si backend no está disponible o falla, usar Supabase directo
        if (!profilesData) {
          console.log('🔍 [CajaChica] Consultando profiles directamente desde Supabase...');
          
          try {
            const result = await safeSupabaseQuery(
              async () => {
                const queryResult = await supabase
                  .from('profiles')
                  .select('id, email, full_name, acceso')
                  .order('full_name', { ascending: true });
                return queryResult;
              },
              {
                timeout: 15000, // 15 segundos para consulta de profiles
                retries: 2 // Reintentar 2 veces
              }
            );
            
            profilesData = result?.data;
            profilesError = result?.error;
            
            if (profilesError) {
              // Si es timeout, manejar específicamente
              if (profilesError.code === 'TIMEOUT_ERROR' || profilesError.message?.includes('timeout')) {
                console.warn('⚠️ [CajaChica] Timeout al consultar profiles directamente');
                profilesError = { message: 'Timeout', code: 'TIMEOUT' };
              }
            }
          } catch (timeoutError: any) {
            if (timeoutError?.message?.includes('timeout')) {
              console.warn('⚠️ [CajaChica] Timeout al consultar profiles directamente');
              profilesError = { message: 'Timeout', code: 'TIMEOUT' };
            } else {
              profilesError = timeoutError;
            }
          }
        }

        if (profilesError) {
          // ✅ No borrar trabajadores si ya existen (evitar que desaparezcan los datos)
          console.warn('⚠️ [CajaChica] Error al cargar trabajadores:', profilesError.message);
          // Solo establecer array vacío si no hay trabajadores cargados previamente
          // Esto evita que los datos desaparezcan si hay un error temporal
          return;
        }

        if (profilesData && profilesData.length > 0) {
          // ✅ ELIMINADO: RPC para obtener roles - usar solo datos de profiles
          // El RPC no funciona, los roles se obtienen desde user_metadata cuando el usuario hace login
          
          // Obtener el usuario actual para acceder a sus metadatos
          const { data: { user: currentUser } } = await supabase.auth.getUser();
          
          // Filtrar perfiles: excluir administradores, contadoras, usuarios sin acceso y específicamente a Antonia
          const trabajadoresFiltrados = profilesData
            .filter((p: any) => {
              // ✅ NUEVO: Verificar que acceso sea true (solo usuarios aprobados)
              if (p.acceso !== true && p.acceso !== 'true' && p.acceso !== 1) {
                return false;
              }
              
              const email = (p.email || '').toLowerCase();
              
              // Excluir específicamente a Antonia (administradora)
              if (email === 'antonia.meza@minesafe.cl') {
                return false;
              }
              
              // ✅ AGREGAR: Excluir específicamente a Ximena (administradora)
              if (email === 'ximena.marin@minesafe.cl') {
                return false;
              }
              
              // Intentar obtener el rol desde user_metadata (RPC eliminado)
              let rol = '';
              if (currentUser && currentUser.id === p.id) {
                rol = (currentUser.user_metadata?.rol || currentUser.user_metadata?.role || '').trim();
              }
              
              // Excluir si es administrador o contadora
              if (rol === 'Administrador' || rol === 'Contadora' || rol === 'administrador' || rol === 'contadora') {
                return false;
              }
              
              // Incluir solo perfiles con acceso=true que no sean admin/contadora
              return true;
            })
            .map((p: any) => ({
              id: p.id,
              nombre: p.full_name || p.email,
              email: p.email
            }));
          
          setTrabajadores(trabajadoresFiltrados);
        } else {
          setTrabajadores([]);
          console.warn('⚠️ [CajaChica] No se encontraron perfiles válidos, trabajadores = []');
        }
      } catch (error) {
        // ✅ No borrar trabajadores si ya existen (evitar que desaparezcan los datos)
        console.error('❌ [CajaChica] Error en cargarTrabajadoresFallback:', error);
        // Solo establecer array vacío si no hay trabajadores cargados previamente
        // Esto evita que los datos desaparezcan si hay un error temporal
        if (trabajadores.length === 0) {
          setTrabajadores([]);
        } else {
          console.warn('⚠️ [CajaChica] Error al recargar trabajadores, manteniendo datos existentes');
        }
      }
    };

    cargarTrabajadores();
  }, [ready]); // ✅ Cambiado: ahora depende de ready

  // Función para cargar gastos desde la base de datos
  const cargarGastos = async () => {
    try {
      console.log('🔄 [CajaChica] Iniciando carga de gastos...');
      console.log('🔍 [CajaChica] Llamando a getGastos()...');
      const resultadoGastos = await getGastos();
      
      console.log('📊 [CajaChica] Resultado de getGastos:', {
        success: resultadoGastos.success,
        dataLength: resultadoGastos.data?.length || 0,
        message: resultadoGastos.message,
        errors: resultadoGastos.errors,
        // 🔍 NUEVO: Verificar si los datos tienen monto_ocr
        primerosGastos: resultadoGastos.data?.slice(0, 3).map((g: any) => ({
          id: g.id,
          fecha: g.fecha,
          monto: g.monto,
          monto_ocr: g.monto_ocr,
          tiene_monto_ocr: g.monto_ocr !== undefined && g.monto_ocr !== null,
          tipo_monto_ocr: typeof g.monto_ocr
        }))
      });
      
      // Cargar gastos pendientes del store offline
      const gastosPendientes = offlineStore.pendingExpenses;
      setPendingExpenses(gastosPendientes);
      
      if (resultadoGastos.success && resultadoGastos.data) {
        console.log(`✅ [CajaChica] ${resultadoGastos.data.length} gastos cargados desde la BD`);
        console.log(`🔍 [CajaChica] Estados de alerta desde BD (primeros 3):`, resultadoGastos.data.slice(0, 3).map((g: any) => ({ id: g.id, estado_alerta: g.estado_alerta })));
        
        
        const gastosMapeados = resultadoGastos.data.map((gasto, index) => {
          // ✅ CORREGIDO: Usar created_at del gasto para obtener la fecha/hora exacta de creación
          // Si no está disponible, usar updated_at, y como último recurso usar la fecha del gasto
          const fechaCreacion = (gasto as any).created_at || 
                                (gasto as any).updated_at || 
                                gasto.fecha || 
                                new Date().toISOString();
          
          // ✅ MEJORADO: Convertir documento a string si es un objeto
          let documentoFinal: string = '';
          if (gasto.documento) {
            if (typeof gasto.documento === 'string') {
              documentoFinal = gasto.documento.trim();
            } else if (typeof gasto.documento === 'object') {
              // Si es un objeto, intentar extraer el valor de propiedades comunes
              const docObj = gasto.documento as any;
              if (docObj.path) {
                documentoFinal = String(docObj.path).trim();
              } else if (docObj.url) {
                documentoFinal = String(docObj.url).trim();
              } else if (docObj.publicUrl) {
                documentoFinal = String(docObj.publicUrl).trim();
              } else {
                console.warn('⚠️ [CajaChica] Campo documento es un objeto sin propiedades conocidas:', gasto.documento);
                documentoFinal = '';
              }
            }
          }
          
          return {
            id: gasto.id || `temp_${index + 1}`, // ✅ Usar el ID real de la BD (UUID), con fallback temporal
            fecha: gasto.fecha,
            trabajador: gasto.trabajador || 'Sin asignar',
            descripcion: gasto.descripcion,
            monto: gasto.monto,
            categoria: gasto.categoria,
            documento: documentoFinal,
            sincronizado: gasto.sincronizado !== undefined ? gasto.sincronizado : true, // Usar valor real de la BD
            aprobado: gasto.aprobado !== undefined ? gasto.aprobado : true, // Los gastos se aprueban automáticamente
            usuarioModificacion: gasto.trabajador || 'sistema',
            fechaModificacion: fechaCreacion, // ✅ Usar la fecha de creación real del gasto
            created_at: (gasto as any).created_at, // ✅ Preservar created_at para auditoría
            updated_at: (gasto as any).updated_at, // ✅ Preservar updated_at para auditoría
            monto_ocr: gasto.monto_ocr, // ✅ NUEVO: Incluir monto_ocr en el mapeo
            estado_alerta: (gasto as any).estado_alerta || null, // ✅ Incluir estado_alerta (null si no existe, no 'pendiente' por defecto)
            historial: [
              { 
                fecha: fechaCreacion, // ✅ Usar fecha de creación real
                accion: 'Creado', 
                usuario: gasto.trabajador || 'sistema' 
              }
            ]
          };
        });
        
        // Agregar gastos pendientes a la lista (convertirlos al formato esperado)
        const gastosPendientesMapeados = gastosPendientes.map((expense, index) => ({
          id: `pending_${expense.idTemp}`, // ID temporal único
          fecha: expense.data.fecha,
          trabajador: expense.data.trabajador || 'Sin asignar',
          descripcion: expense.data.descripcion,
          monto: expense.data.monto,
          categoria: expense.data.categoria,
          documento: expense.data.documento || '',
          sincronizado: false, // Pendiente de sincronizar
          aprobado: false, // Pendiente
          usuarioModificacion: expense.data.trabajador || 'sistema',
          fechaModificacion: expense.createdAt,
          historial: [
            { 
              fecha: expense.createdAt, 
              accion: 'Creado (offline)', 
              usuario: expense.data.trabajador || 'sistema' 
            }
          ],
          isPending: true, // Marca para identificar gastos pendientes
          idTemp: expense.idTemp // Guardar ID temporal
        }));
        
        // Combinar gastos sincronizados con pendientes
        const todosLosGastos = [...gastosMapeados, ...gastosPendientesMapeados];
        // Logs removidos para reducir ruido en consola
        if (false) {
          console.warn('⚠️ [CajaChica] setGastos - NO HAY gastos con monto_ocr. Muestra primeros 2 gastos:', todosLosGastos.slice(0, 2).map((g: any) => ({
            id: g.id,
            fecha: g.fecha,
            monto: g.monto,
            monto_ocr: g.monto_ocr,
            todas_las_propiedades: Object.keys(g)
          })));
        }
        // ✅ MEJORADO: Fusionar gastos de BD con gastos locales (agregados recientemente)
        // Esto preserva los gastos que se agregaron localmente hasta que estén confirmados en la BD
        // Soluciona el problema de que los gastos desaparecen temporalmente en producción debido a:
        // - Caché del backend (5 minutos)
        // - Latencia de red (Netlify → Railway → Supabase)
        // - Timing: el gasto puede no estar disponible inmediatamente después de crearse
        setGastos(prevGastos => {
          // Crear Map con gastos de BD (fuente de verdad, prioridad alta)
          const gastosBDMap = new Map(todosLosGastos.map(g => [g.id, g]));
          
          // Agregar gastos locales que no están en BD todavía
          const gastosLocales = prevGastos.filter(g => {
            // Si ya está en BD, no incluirlo (evitar duplicado, usar el de BD que es más actualizado)
            if (gastosBDMap.has(g.id)) {
              console.log(`✅ [CajaChica] Gasto ${g.id} ya está en BD, usando versión de BD`);
              return false;
            }
            
            // ✅ MEJORADO: Aumentar tiempo a 15 segundos para manejar latencia de producción
            // Esto preserva gastos agregados localmente hasta que estén en la BD
            // En producción puede haber latencia: Netlify → Railway → Supabase → Backend cache
            const fechaCreacionLocal = (g as any).fechaCreacionLocal;
            const fueAgregadoRecientemente = fechaCreacionLocal && 
              (Date.now() - new Date(fechaCreacionLocal).getTime()) < 15000; // 15 segundos
            
            // También incluir gastos pendientes offline
            const esPendienteOffline = (g as any).isPending === true;
            
            if (fueAgregadoRecientemente || esPendienteOffline) {
              console.log(`🔄 [CajaChica] Preservando gasto local ${g.id}:`, {
                fechaCreacionLocal,
                tiempoTranscurrido: fechaCreacionLocal ? Date.now() - new Date(fechaCreacionLocal).getTime() : 'N/A',
                esPendienteOffline
              });
            }
            
            return fueAgregadoRecientemente || esPendienteOffline;
          });
          
          // Combinar: BD primero (fuente de verdad), luego locales (sin duplicados)
          const gastosFusionados = [...todosLosGastos, ...gastosLocales];
          
          console.log('🔄 [CajaChica] Gastos fusionados:', {
            totalBD: todosLosGastos.length,
            totalLocales: gastosLocales.length,
            totalFusionados: gastosFusionados.length,
            gastosLocalesPreservados: gastosLocales.map(g => ({
              id: g.id,
              fechaCreacionLocal: (g as any).fechaCreacionLocal
            }))
          });
          
          return gastosFusionados;
        });
        console.log(`✅ [CajaChica] Total de gastos establecidos: ${gastosMapeados.length + gastosPendientesMapeados.length}`);
      } else {
        console.warn('⚠️ [CajaChica] getGastos no fue exitoso o no hay datos:', {
          success: resultadoGastos.success,
          message: resultadoGastos.message,
          errors: resultadoGastos.errors
        });
        
        // Si hay errores específicos, mostrarlos
        if (resultadoGastos.errors && resultadoGastos.errors.length > 0) {
          console.error('❌ [CajaChica] Errores al cargar gastos:', resultadoGastos.errors);
          
          // Si es un error de timeout o RLS, mostrar mensaje más específico
          const errorMessage = resultadoGastos.errors[0];
          if (errorMessage.includes('Timeout') || errorMessage.includes('timeout')) {
            console.error('💡 [CajaChica] Problema de timeout - Verifica:');
            console.error('   1. Políticas RLS de la tabla "gastos" en Supabase');
            console.error('   2. Conectividad con Supabase');
            console.error('   3. Que la tabla "gastos" exista y tenga datos');
          } else if (errorMessage.includes('RLS') || errorMessage.includes('permission') || errorMessage.includes('permisos')) {
            console.error('💡 [CajaChica] Problema de permisos RLS - Verifica en Supabase Dashboard:');
            console.error('   1. Authentication → Policies → Tabla "gastos"');
            console.error('   2. Debe haber una política que permita SELECT a usuarios autenticados');
            console.error('   3. Si eres administrador, la política debe permitir ver todos los gastos');
          }
        }
        
        // ✅ CORREGIDO: Preservar gastos existentes si ya hay datos cargados
        const gastosExistentes = gastos.filter(g => !g.isPending); // Gastos sincronizados existentes
        const gastosPendientesActuales = offlineStore.pendingExpenses;
        
        if (gastosExistentes.length > 0) {
          console.warn('⚠️ [CajaChica] Error al recargar gastos, manteniendo datos existentes:', gastosExistentes.length);
          // Mantener gastos existentes + agregar nuevos pendientes
          const nuevosPendientes = gastosPendientesActuales
            .filter(exp => !gastos.some(g => g.idTemp === exp.idTemp))
            .map((expense) => ({
              id: `pending_${expense.idTemp}`,
              fecha: expense.data.fecha,
              trabajador: expense.data.trabajador || 'Sin asignar',
              descripcion: expense.data.descripcion,
              monto: expense.data.monto,
              categoria: expense.data.categoria,
              documento: expense.data.documento || '',
              sincronizado: false,
              aprobado: false,
              usuarioModificacion: expense.data.trabajador || 'sistema',
              fechaModificacion: expense.createdAt,
              historial: [
                { 
                  fecha: expense.createdAt, 
                  accion: 'Creado (offline)', 
                  usuario: expense.data.trabajador || 'sistema' 
                }
              ],
              isPending: true,
              idTemp: expense.idTemp
            }));
          
          setGastos([...gastosExistentes, ...nuevosPendientes]);
        } else {
          // Solo si no hay gastos existentes, mostrar solo pendientes
          const gastosPendientesMapeados = gastosPendientesActuales.map((expense) => ({
            id: `pending_${expense.idTemp}`,
            fecha: expense.data.fecha,
            trabajador: expense.data.trabajador || 'Sin asignar',
            descripcion: expense.data.descripcion,
            monto: expense.data.monto,
            categoria: expense.data.categoria,
            documento: expense.data.documento || '',
            sincronizado: false,
            aprobado: false,
            usuarioModificacion: expense.data.trabajador || 'sistema',
            fechaModificacion: expense.createdAt,
            historial: [
              { 
                fecha: expense.createdAt, 
                accion: 'Creado (offline)', 
                usuario: expense.data.trabajador || 'sistema' 
              }
            ],
            isPending: true,
            idTemp: expense.idTemp
          }));
          
          setGastos(gastosPendientesMapeados);
        }
      }
    } catch (error) {
      console.error('❌ [CajaChica] Error al cargar gastos:', error);
      
      // ✅ MEJORADO: Preservar TODOS los gastos existentes en caso de error
      // Incluir tanto gastos sincronizados como gastos agregados localmente recientemente
      const gastosExistentes = gastos.filter(g => {
        // Excluir solo gastos pendientes offline antiguos
        const esPendienteOffline = (g as any).isPending === true;
        
        // Incluir gastos agregados localmente recientemente (menos de 15 segundos)
        const fechaCreacionLocal = (g as any).fechaCreacionLocal;
        const fueAgregadoRecientemente = fechaCreacionLocal && 
          (Date.now() - new Date(fechaCreacionLocal).getTime()) < 15000;
        
        // Preservar si NO es pendiente offline O si fue agregado recientemente
        return !esPendienteOffline || fueAgregadoRecientemente;
      });
      
      if (gastosExistentes.length > 0) {
        console.warn('⚠️ [CajaChica] Error al recargar gastos, manteniendo datos existentes:', {
          totalGastos: gastosExistentes.length,
          gastosPreservados: gastosExistentes.map(g => ({
            id: g.id,
            fechaCreacionLocal: (g as any).fechaCreacionLocal,
            isPending: (g as any).isPending
          }))
        });
        // Mantener gastos existentes + agregar nuevos pendientes offline
        const gastosPendientesActuales = offlineStore.pendingExpenses;
        const nuevosPendientes = gastosPendientesActuales
          .filter(exp => !gastos.some(g => g.idTemp === exp.idTemp))
          .map((expense) => ({
            id: `pending_${expense.idTemp}`,
            fecha: expense.data.fecha,
            trabajador: expense.data.trabajador || 'Sin asignar',
            descripcion: expense.data.descripcion,
            monto: expense.data.monto,
            categoria: expense.data.categoria,
            documento: expense.data.documento || '',
            sincronizado: false,
            aprobado: false,
            usuarioModificacion: expense.data.trabajador || 'sistema',
            fechaModificacion: expense.createdAt,
            historial: [
              { 
                fecha: expense.createdAt, 
                accion: 'Creado (offline)', 
                usuario: expense.data.trabajador || 'sistema' 
              }
            ],
            isPending: true,
            idTemp: expense.idTemp
          }));
        
        setGastos([...gastosExistentes, ...nuevosPendientes]);
      } else {
        // Solo si no hay gastos existentes, mostrar solo pendientes offline
        const gastosPendientes = offlineStore.pendingExpenses;
        setPendingExpenses(gastosPendientes);
        
        const gastosPendientesMapeados = gastosPendientes.map((expense) => ({
          id: `pending_${expense.idTemp}`,
          fecha: expense.data.fecha,
          trabajador: expense.data.trabajador || 'Sin asignar',
          descripcion: expense.data.descripcion,
          monto: expense.data.monto,
          categoria: expense.data.categoria,
          documento: expense.data.documento || '',
          sincronizado: false,
          aprobado: false,
          usuarioModificacion: expense.data.trabajador || 'sistema',
          fechaModificacion: expense.createdAt,
          historial: [
            { 
              fecha: expense.createdAt, 
              accion: 'Creado (offline)', 
              usuario: expense.data.trabajador || 'sistema' 
            }
          ],
          isPending: true,
          idTemp: expense.idTemp
        }));
        
        setGastos(gastosPendientesMapeados);
      }
    }
  };

  // ✅ OPTIMIZADO: Cargar gastos después de mostrar UI (carga diferida)
  useEffect(() => {
    if (!ready) return; // Esperar a que la UI esté lista
    
    cargarGastos();
    
    // Suscribirse a cambios en el store offline
    const unsubscribe = offlineStore.subscribe(() => {
      cargarGastos();
    });
    
    return () => {
      unsubscribe();
    };
  }, [ready]); // ✅ Cambiado: ahora depende de ready

  // Establecer trabajador automáticamente cuando se abre el modal para usuarios trabajadores
  useEffect(() => {
    if (mostrarModal && userRole === 'Usuario') {
      const trabajadorAuto = user?.nombre || user?.correo?.split('@')[0] || '';
      if (trabajadorAuto) {
        setNuevoGasto(prev => {
          // Solo actualizar si el trabajador no está establecido o está vacío
          if (!prev.trabajador || prev.trabajador === '') {
            return {
              ...prev,
              trabajador: trabajadorAuto
            };
          }
          return prev;
        });
      }
    }
  }, [mostrarModal, userRole, user]);

  // ✅ CORREGIDO: Cargar auditoría solo desde gastos, sin consultar tabla auditoria
  useEffect(() => {
    const cargarAuditoria = async () => {
      if (!mostrarAuditoria) return; // Solo cargar cuando se muestre la auditoría
      
      setCargandoAuditoria(true);
      try {
        // ✅ COMENTADO: No hacer SELECT a tabla auditoria, solo generar desde gastos
        /*
        const { data, error } = await supabase
          .from('auditoria')
          .select('*')
          .order('fecha', { ascending: false })
          .limit(50); // Limitar a los últimos 50 registros

        if (error) {
          // Si la tabla no existe (404) o hay error, generar eventos basados en gastos
          // No mostrar error en consola si es un 404 (tabla no existe) - es esperado
          const isTableNotFound = 
            error.code === 'PGRST116' || 
            error.code === '42P01' ||
            (error.message && (
              error.message.includes('does not exist') ||
              (error.message.includes('relation') && error.message.includes('does not exist')) ||
              error.message.includes('no existe')
            ));
          
          if (!isTableNotFound) {
            // Solo loguear errores que no sean "tabla no existe"
            console.warn('Error al cargar auditoría (no crítico):', error.message || error);
          }
          generarEventosDesdeGastos();
        } else {
          // Mapear datos de Supabase al formato esperado
          const auditoriaMapeada = (data || []).map((log: any) => ({
            id: log.id || `log-${Date.now()}-${Math.random()}`,
            usuario: log.usuario || 'Sistema',
            accion: log.accion || 'Acción',
            fecha: log.fecha || log.created_at || new Date().toISOString(),
            detalle: log.detalle || 'Sin detalle',
            resultado: 'OK' // Asumir OK si se guardó correctamente
          }));
          
          // Si no hay eventos en la tabla, generar eventos desde gastos
          if (auditoriaMapeada.length === 0) {
            generarEventosDesdeGastos();
          } else {
            setAuditoriaReal(auditoriaMapeada);
          }
        }
        */
        
        // ✅ CORREGIDO: Solo generar eventos desde gastos, sin consultar tabla auditoria
        generarEventosDesdeGastos();
      } catch (error) {
        // Generar eventos desde gastos si hay error
        generarEventosDesdeGastos();
      } finally {
        setCargandoAuditoria(false);
      }
    };

    // Función para generar eventos de auditoría desde los gastos existentes
    const generarEventosDesdeGastos = () => {
      console.log(`🔍 [Auditoría] Generando eventos desde ${gastos.length} gastos`);
      
      const eventosGenerados = gastos
        .slice(0, 20) // Limitar a los últimos 20 gastos
        .map((gasto, index) => {
          // ✅ CORREGIDO: Priorizar created_at para obtener la fecha/hora EXACTA de creación
          // Esto asegura que cada gasto muestre su fecha/hora real de creación
          let fechaConHora: string;
          
          // Prioridad 1: created_at (fecha exacta de creación en la BD)
          if ((gasto as any).created_at) {
            fechaConHora = (gasto as any).created_at;
          } 
          // Prioridad 2: updated_at (si created_at no está disponible)
          else if ((gasto as any).updated_at) {
            fechaConHora = (gasto as any).updated_at;
          } 
          // Prioridad 3: fechaModificacion (si se preservó al mapear desde la BD)
          else if ((gasto as any).fechaModificacion && (gasto as any).fechaModificacion !== new Date().toISOString()) {
            // Solo usar fechaModificacion si NO es la fecha actual (evitar usar el fallback incorrecto)
            fechaConHora = (gasto as any).fechaModificacion;
          } 
          // Prioridad 4: fecha del gasto (solo fecha, sin hora) - usar inicio del día
          else if (gasto.fecha) {
            const fechaBase = new Date(gasto.fecha);
            fechaBase.setHours(0, 0, 0, 0); // Inicio del día
            fechaConHora = fechaBase.toISOString();
            console.warn(`⚠️ [Auditoría] Gasto ${gasto.id} sin timestamp, usando fecha sin hora`);
          } 
          // Último recurso: fecha actual (no debería llegar aquí)
          else {
            fechaConHora = new Date().toISOString();
            console.warn(`⚠️ [Auditoría] Gasto ${gasto.id} sin fecha, usando fecha actual`);
          }
          
          console.log(`📅 [Auditoría] Gasto ${gasto.id}: ${fechaConHora}`);
          
          return {
            id: `gasto-${gasto.id}-${index}`,
            usuario: gasto.trabajador || 'Sistema',
            accion: 'CREAR',
            fecha: fechaConHora, // ✅ Fecha/hora exacta de creación del gasto
            detalle: `Gasto registrado: ${gasto.descripcion} - Monto: $${gasto.monto.toLocaleString('es-CL')} - Categoría: ${gasto.categoria}`,
            resultado: gasto.sincronizado ? 'OK' : 'PENDIENTE'
          };
        })
        .sort((a, b) => {
          const fechaA = new Date(a.fecha).getTime();
          const fechaB = new Date(b.fecha).getTime();
          return fechaB - fechaA; // Ordenar descendente (más recientes primero)
        });

      setAuditoriaReal(eventosGenerados);
    };

    cargarAuditoria();
  }, [mostrarAuditoria, gastos]);

  // 📊 CÁLCULOS DINÁMICOS DESDE GASTOS DE LA API MOCK
  // IMPORTANTE: Todos los hooks y cálculos deben estar antes del return condicional
  
  // Presupuesto mensual por trabajador
  const PRESUPUESTO_MENSUAL_POR_TRABAJADOR = 400000; // 400.000 CLP por trabajador
  // Presupuesto total mensual (suma de todos los trabajadores)
  const PRESUPUESTO_MENSUAL = trabajadores.length > 0 
    ? trabajadores.length * PRESUPUESTO_MENSUAL_POR_TRABAJADOR 
    : PRESUPUESTO_MENSUAL_POR_TRABAJADOR; // Fallback si no hay trabajadores cargados

  // Función helper para normalizar fechas
  const normalizarFecha = (fecha: string | Date | undefined): Date | null => {
    if (!fecha) return null;
    
    try {
      if (fecha instanceof Date) {
        return isNaN(fecha.getTime()) ? null : fecha;
      }
      
      if (typeof fecha === 'string') {
        // ✅ MEJORADO: Manejar fechas en formato YYYY-MM-DD sin hora
        // Si es solo fecha (sin hora), crear Date en zona local para evitar problemas de UTC
        if (/^\d{4}-\d{2}-\d{2}$/.test(fecha)) {
          // Formato YYYY-MM-DD, parsear manualmente para evitar problemas de zona horaria
          const [año, mes, dia] = fecha.split('-').map(Number);
          const fechaLocal = new Date(año, mes - 1, dia);
          return fechaLocal;
        }
        
        // Para otros formatos, usar el parser estándar
        const parsed = new Date(fecha);
        if (!isNaN(parsed.getTime())) {
          return parsed;
        }
      }
    } catch {
      return null;
    }
    
    return null;
  };

  // Función para obtener rango de fechas según período seleccionado
  const obtenerRangoPeriodo = () => {
    const fechaActual = new Date();
    const añoActual = fechaActual.getFullYear();
    
    // Mapeo de meses en español a índices (0-11)
    const mesesMap: Record<string, number> = {
      'enero': 0,
      'febrero': 1,
      'marzo': 2,
      'abril': 3,
      'mayo': 4,
      'junio': 5,
      'julio': 6,
      'agosto': 7,
      'septiembre': 8,
      'octubre': 9,
      'noviembre': 10,
      'diciembre': 11
    };
    
    if (periodoFiltro === 'personalizado') {
      // Rango personalizado desde calendario
      if (fechaInicioPersonalizada && fechaFinPersonalizada) {
        const inicio = new Date(fechaInicioPersonalizada);
        const fin = new Date(fechaFinPersonalizada);
        fin.setHours(23, 59, 59, 999);
        return { inicio, fin };
      }
      // Si no hay fechas seleccionadas, usar todo el año actual
      return {
        inicio: new Date(añoActual, 0, 1),
        fin: new Date(añoActual, 11, 31, 23, 59, 59, 999)
      };
    } else if (mesesMap[periodoFiltro] !== undefined) {
      // Mes específico del año actual
      const mesIndex = mesesMap[periodoFiltro];
      const inicio = new Date(añoActual, mesIndex, 1);
      const fin = new Date(añoActual, mesIndex + 1, 0, 23, 59, 59, 999);
      return { inicio, fin };
    } else if (periodoFiltro === 'trimestre') {
      // Último trimestre completo
      const trimestreActual = Math.ceil((fechaActual.getMonth() + 1) / 3);
      const año = fechaActual.getFullYear();
      
      if (trimestreActual === 1) {
        return {
          inicio: new Date(año - 1, 9, 1),
          fin: new Date(año - 1, 11, 31)
        };
      } else {
        const mesInicio = (trimestreActual - 2) * 3;
        const mesFin = mesInicio + 2;
        return {
          inicio: new Date(año, mesInicio, 1),
          fin: new Date(año, mesFin + 1, 0)
        };
      }
    } else if (periodoFiltro === 'año') {
      // Año actual completo
      const año = fechaActual.getFullYear();
      return {
        inicio: new Date(año, 0, 1), // 1 de enero del año actual
        fin: new Date(año, 11, 31, 23, 59, 59, 999) // 31 de diciembre del año actual, fin del día
      };
    } else {
      // Fallback: año actual completo
      const año = fechaActual.getFullYear();
      return {
        inicio: new Date(año, 0, 1),
        fin: new Date(año, 11, 31, 23, 59, 59, 999)
      };
    }
  };

  // Obtener rango de período si está activo (debe estar antes de todos los cálculos)
  const rangoPeriodo = obtenerRangoPeriodo();
  
  // Función helper para excluir gastos rechazados Y pendientes de las métricas
  // ✅ CRÍTICO: Solo contar gastos APROBADOS en las métricas consolidadas
  // Los gastos pendientes y rechazados NO se incluyen en:
  // - Gasto del Mes
  // - Saldo Disponible
  // - Distribución por Categoría
  // - Cualquier otra métrica consolidada
  const excluirGastosRechazados = (gastos: any[]) => {
    return gastos.filter((gasto: any) => {
      const estadoAlerta = (gasto.estado_alerta || '').toString().toLowerCase().trim();
      
      // ✅ MEJORADO: Validación más estricta y explícita
      // Excluir explícitamente: rechazados, pendientes, o cualquier valor que no sea 'aprobado'
      if (estadoAlerta === 'rechazado' || estadoAlerta === 'pendiente') {
        console.log(`❌ [excluirGastosRechazados] Gasto ${gasto.id} EXCLUIDO de métricas - estado: ${estadoAlerta}`);
        return false;
      }
      
      // ✅ CRÍTICO: Solo incluir si es explícitamente 'aprobado' o null/undefined (gastos antiguos)
      // Cualquier otro valor (incluyendo strings vacíos, 'null', etc.) se excluye
      const esAprobado = estadoAlerta === 'aprobado';
      const esGastoAntiguo = !gasto.estado_alerta || gasto.estado_alerta === null || gasto.estado_alerta === undefined;
      
      if (esAprobado || esGastoAntiguo) {
        console.log(`✅ [excluirGastosRechazados] Gasto ${gasto.id} INCLUIDO en métricas - estado: ${estadoAlerta || 'null/undefined (gasto antiguo)'}`);
        return true;
      }
      
      // ✅ NUEVO: Excluir cualquier otro estado no reconocido
      console.warn(`⚠️ [excluirGastosRechazados] Gasto ${gasto.id} EXCLUIDO - estado no reconocido: "${estadoAlerta}"`);
      return false;
    });
  };
  
  // Memoizar gastosFiltradosPorPeriodo para evitar recálculos innecesarios
  const gastosFiltradosPorPeriodo = useMemo(() => {
    const usarPeriodo = periodoFiltro !== 'personalizado' || (periodoFiltro === 'personalizado' && fechaInicioPersonalizada && fechaFinPersonalizada);
    
    // ✅ DEBUG: Log del rango de período
    console.log('🔍 [CajaChica] Rango de período:', {
      periodoFiltro,
      rangoPeriodo: {
        inicio: rangoPeriodo.inicio.toISOString(),
        fin: rangoPeriodo.fin.toISOString()
      },
      totalGastos: gastos.length,
      usarPeriodo
    });
    
    const gastosFiltrados = gastos.filter((gasto: any) => {
      if (usarPeriodo) {
        const fechaGasto = normalizarFecha(gasto.fecha);
        if (!fechaGasto) {
          console.log('❌ [CajaChica] Gasto sin fecha válida:', {
            id: gasto.id,
            fecha: gasto.fecha,
            trabajador: gasto.trabajador,
            estado_alerta: gasto.estado_alerta
          });
          return false;
        }
        
        // ✅ MEJORADO: Normalizar fechas a medianoche para comparación correcta
        // Comparar solo la parte de fecha (sin hora) para evitar problemas de zona horaria
        const fechaGastoNormalizada = new Date(fechaGasto.getFullYear(), fechaGasto.getMonth(), fechaGasto.getDate());
        const inicioNormalizado = new Date(rangoPeriodo.inicio.getFullYear(), rangoPeriodo.inicio.getMonth(), rangoPeriodo.inicio.getDate());
        const finNormalizado = new Date(rangoPeriodo.fin.getFullYear(), rangoPeriodo.fin.getMonth(), rangoPeriodo.fin.getDate());
        
        const dentroDelPeriodo = fechaGastoNormalizada >= inicioNormalizado && fechaGastoNormalizada <= finNormalizado;
        
        if (!dentroDelPeriodo) {
          console.log('❌ [CajaChica] Gasto fuera del período:', {
            id: gasto.id,
            fechaOriginal: gasto.fecha,
            fechaGasto: fechaGasto.toISOString(),
            fechaGastoNormalizada: fechaGastoNormalizada.toISOString(),
            trabajador: gasto.trabajador,
            estado_alerta: gasto.estado_alerta,
            monto: gasto.monto,
            rangoInicio: rangoPeriodo.inicio.toISOString(),
            rangoInicioNormalizado: inicioNormalizado.toISOString(),
            rangoFin: rangoPeriodo.fin.toISOString(),
            rangoFinNormalizado: finNormalizado.toISOString(),
            antesDelInicio: fechaGastoNormalizada < inicioNormalizado,
            despuesDelFin: fechaGastoNormalizada > finNormalizado
          });
        }
        return dentroDelPeriodo;
      }
      return true;
    });
    
    console.log('🔍 [CajaChica] Gastos filtrados por período:', {
      totalFiltrados: gastosFiltrados.length,
      gastos: gastosFiltrados.map(g => ({
        id: g.id,
        fecha: g.fecha,
        fechaNormalizada: normalizarFecha(g.fecha)?.toISOString(),
        trabajador: g.trabajador,
        monto: g.monto,
        estado_alerta: g.estado_alerta
      }))
    });
    
    // ✅ Excluir gastos rechazados de las métricas
    const gastosSinRechazados = excluirGastosRechazados(gastosFiltrados);
    
    console.log('🔍 [CajaChica] Gastos después de excluir rechazados y pendientes:', {
      total: gastosSinRechazados.length,
      totalFiltradosPorPeriodo: gastosFiltrados.length,
      excluidos: gastosFiltrados.length - gastosSinRechazados.length,
      gastosIncluidos: gastosSinRechazados.map(g => ({
        id: g.id,
        fecha: g.fecha,
        monto: g.monto,
        estado_alerta: g.estado_alerta,
        trabajador: g.trabajador
      })),
      gastosExcluidos: gastosFiltrados
        .filter(g => {
          const estadoAlerta = g.estado_alerta;
          return estadoAlerta === 'rechazado' || estadoAlerta === 'pendiente';
        })
        .map(g => ({
          id: g.id,
          fecha: g.fecha,
          monto: g.monto,
          estado_alerta: g.estado_alerta,
          trabajador: g.trabajador
        }))
    });
    
    return gastosSinRechazados;
  }, [gastos, periodoFiltro, fechaInicioPersonalizada, fechaFinPersonalizada, rangoPeriodo]);

  // Aplicar filtros adicionales (trabajador, categoría, monto) sobre los gastos del período
  // Si el usuario es Trabajador (rol "Usuario"), solo mostrar sus propios gastos
  
  // ✅ DEBUG: Log antes del filtro para ver qué está pasando
  console.log('🔍 [CajaChica] ANTES del filtro de trabajador:', {
    userRole,
    totalGastosPeriodo: gastosFiltradosPorPeriodo.length,
    user: {
      nombre: user?.nombre,
      correo: user?.correo,
      rol: user?.rol
    },
    primerosGastos: gastosFiltradosPorPeriodo.slice(0, 2).map(g => ({
      id: g.id,
      trabajador: g.trabajador,
      fecha: g.fecha
    }))
  });
  
  const gastosFiltrados = gastosFiltradosPorPeriodo.filter(gasto => {
    if (userRole === 'Usuario') {
      // ✅ MEJORADO: Obtener todas las posibles variantes del nombre del usuario
      const nombreUsuario = (user?.nombre || '').trim();
      const emailUsuario = (user?.correo || '').trim();
      const emailSinDominio = emailUsuario.split('@')[0] || '';
      const trabajadorGasto = (gasto.trabajador || '').trim();
      
      // ✅ DEBUG: Log detallado para entender qué está pasando
      console.log('🔍 [CajaChica] Filtrando gasto para trabajador:', {
        gastoId: gasto.id,
        nombreUsuario,
        emailUsuario,
        emailSinDominio,
        trabajadorGasto,
        userObject: {
          nombre: user?.nombre,
          correo: user?.correo,
          rol: user?.rol
        }
      });
      
      // Si no hay trabajador en el gasto, excluirlo
      if (!trabajadorGasto) {
        console.log('❌ [CajaChica] Gasto excluido: sin trabajador asignado');
        return false;
      }
      
      // Normalizar para comparación (minúsculas, sin espacios extra)
      const normalizar = (str: string) => str.toLowerCase().trim();
      
      // ✅ NUEVO: Normalización flexible que elimina puntos, espacios, guiones
      // Esto permite que "lucas.serrano" coincida con "Lucas Serrano"
      const normalizarFlexible = (str: string) => {
        return str
          .toLowerCase()
          .trim()
          .replace(/[.\s\-_]/g, ''); // Eliminar puntos, espacios, guiones y guiones bajos
      };
      
      // Comparar con nombre completo
      const coincideNombre = nombreUsuario && 
        normalizar(nombreUsuario) === normalizar(trabajadorGasto);
      
      // Comparar con email sin dominio
      const coincideEmail = emailSinDominio && 
        normalizar(emailSinDominio) === normalizar(trabajadorGasto);
      
      // ✅ NUEVO: Comparación flexible que permite coincidencias como:
      // "lucas.serrano" === "Lucas Serrano" (ambos se normalizan a "lucasserrano")
      const coincideNombreFlexible = nombreUsuario && 
        normalizarFlexible(nombreUsuario) === normalizarFlexible(trabajadorGasto);
      
      const coincideEmailFlexible = emailSinDominio && 
        normalizarFlexible(emailSinDominio) === normalizarFlexible(trabajadorGasto);
      
      // Si no coincide con ninguna variante, excluir
      if (!coincideNombre && !coincideEmail && !coincideNombreFlexible && !coincideEmailFlexible) {
        console.log('❌ [CajaChica] Gasto excluido - No coincide:', {
          nombreUsuario,
          emailSinDominio,
          trabajadorGasto,
          coincideNombre,
          coincideEmail,
          coincideNombreFlexible,
          coincideEmailFlexible,
          nombreNormalizado: nombreUsuario ? normalizar(nombreUsuario) : 'N/A',
          emailNormalizado: emailSinDominio ? normalizar(emailSinDominio) : 'N/A',
          trabajadorNormalizado: normalizar(trabajadorGasto),
          nombreFlexible: nombreUsuario ? normalizarFlexible(nombreUsuario) : 'N/A',
          emailFlexible: emailSinDominio ? normalizarFlexible(emailSinDominio) : 'N/A',
          trabajadorFlexible: normalizarFlexible(trabajadorGasto)
        });
        return false;
      }
      
      console.log('✅ [CajaChica] Gasto incluido - Coincide:', {
        trabajadorGasto,
        coincideNombre,
        coincideEmail,
        coincideNombreFlexible,
        coincideEmailFlexible
      });
    }
    
    // Aplicar filtros manuales (solo para Admin/Contadora)
    if (filtros.trabajador && !gasto.trabajador.toLowerCase().includes(filtros.trabajador.toLowerCase())) return false;
    if (filtros.categoria !== 'Todas' && gasto.categoria !== filtros.categoria) return false;
    if (filtros.montoMin && gasto.monto < desformatearMonto(filtros.montoMin)) return false;
    if (filtros.montoMax && gasto.monto > desformatearMonto(filtros.montoMax)) return false;
    return true;
  });
  
  // ✅ DEBUG: Log después del filtro
  console.log('🔍 [CajaChica] DESPUÉS del filtro de trabajador:', {
    totalGastosFiltrados: gastosFiltrados.length,
    userRole,
    gastosFiltrados: gastosFiltrados.map(g => ({
      id: g.id,
      trabajador: g.trabajador
    }))
  });
  
  // ✅ MODIFICADO: Obtener gastos pendientes del trabajador
  // ✅ CORREGIDO: Solo incluir gastos PENDIENTES, NO rechazados
  // Los gastos rechazados NO deben aparecer en el panel de pendientes
  // Estos gastos NO se incluyen en las métricas (ya están excluidos por excluirGastosRechazados)
  const gastosPendientesTrabajador = useMemo(() => {
    if (userRole !== 'Usuario') {
      return []; // Solo para trabajadores
    }
    
    // ✅ CORREGIDO: Filtrar solo anomalías PENDIENTES (no rechazadas)
    // Los gastos rechazados NO deben aparecer en el panel de pendientes
    const anomaliasPendientes = anomalias.filter(a => a.estado === 'pendiente');
    
    // Obtener IDs de gastos con anomalías pendientes (solo pendientes, no rechazadas)
    const gastosConAnomaliasIds = new Set(
      anomaliasPendientes.map(a => String(a.gasto.id))
    );
    
    // Filtrar desde 'gastos' (no desde gastosFiltradosPorPeriodo que ya excluye pendientes)
    // y aplicar filtro de período manualmente
    const nombreUsuario = (user?.nombre || '').trim();
    const emailUsuario = (user?.correo || '').trim();
    const emailSinDominio = emailUsuario.split('@')[0] || '';
    
    // ✅ MEJORADO: Función de normalización más robusta y flexible
    const normalizar = (str: string) => {
      if (!str) return '';
      return str
        .toLowerCase()
        .trim()
        .replace(/\./g, '') // Eliminar puntos
        .replace(/\s+/g, '') // Eliminar espacios
        .replace(/-/g, '') // Eliminar guiones
        .replace(/_/g, ''); // Eliminar guiones bajos
    };
    
    // ✅ NUEVO: Logs de depuración
    console.log('🔍 [Pendientes] Filtrado de gastos pendientes:', {
      totalGastos: gastos.length,
      nombreUsuario,
      emailSinDominio,
      rangoPeriodo,
      anomaliasPendientes: anomaliasPendientes.length,
      gastosConAnomaliasIds: Array.from(gastosConAnomaliasIds)
    });
    
    const gastosFiltrados = gastos.filter((gasto: any) => {
      // 1. Filtrar por período
      const fechaGasto = normalizarFecha(gasto.fecha);
      if (!fechaGasto) {
        console.log(`❌ [Pendientes] Gasto ${gasto.id} sin fecha válida`);
        return false;
      }
      const dentroDelPeriodo = fechaGasto >= rangoPeriodo.inicio && fechaGasto <= rangoPeriodo.fin;
      if (!dentroDelPeriodo) {
        console.log(`❌ [Pendientes] Gasto ${gasto.id} fuera del período: ${fechaGasto}`);
        return false;
      }
      
      // 2. Verificar que sea del trabajador actual (más flexible)
      const trabajadorGasto = (gasto.trabajador || '').trim();
      if (!trabajadorGasto) {
        console.log(`❌ [Pendientes] Gasto ${gasto.id} sin trabajador`);
        return false;
      }
      
      const nombreNormalizado = normalizar(nombreUsuario);
      const emailNormalizado = normalizar(emailSinDominio);
      const trabajadorNormalizado = normalizar(trabajadorGasto);
      
      const coincideNombre = nombreNormalizado && nombreNormalizado === trabajadorNormalizado;
      const coincideEmail = emailNormalizado && emailNormalizado === trabajadorNormalizado;
      
      if (!coincideNombre && !coincideEmail) {
        console.log(`❌ [Pendientes] Gasto ${gasto.id} no coincide con trabajador:`, {
          trabajadorGasto,
          nombreUsuario,
          emailSinDominio,
          coincideNombre,
          coincideEmail,
          nombreNormalizado,
          trabajadorNormalizado
        });
        return false;
      }
      
      // 3. Verificar estado pendiente
      const tieneAnomaliaPendiente = gastosConAnomaliasIds.has(String(gasto.id));
      const estadoAlerta = (gasto as any).estado_alerta;
      const tieneEstadoPendiente = estadoAlerta === 'pendiente';
      
      if (!tieneAnomaliaPendiente && !tieneEstadoPendiente) {
        console.log(`❌ [Pendientes] Gasto ${gasto.id} no tiene estado pendiente:`, {
          estadoAlerta,
          tieneAnomaliaPendiente
        });
        return false;
      }
      
      console.log(`✅ [Pendientes] Gasto ${gasto.id} incluido en panel de pendientes`);
      return true;
    });
    
    console.log(`📊 [Pendientes] Total gastos pendientes encontrados: ${gastosFiltrados.length}`);
    
    return gastosFiltrados;
  }, [gastos, anomalias, userRole, user, rangoPeriodo]);
  
  // 💰 Gasto del Período Seleccionado: suma de todos los montos del período
  // ✅ IMPORTANTE: Solo incluye gastos APROBADOS (excluye pendientes y rechazados)
  // ✅ VALIDACIÓN ADICIONAL: Verificar que ningún gasto pendiente se incluya
  const gastoMesActual = gastosFiltradosPorPeriodo
    .filter((g: any) => {
      const estadoAlerta = (g.estado_alerta || '').toString().toLowerCase().trim();
      const esPendiente = estadoAlerta === 'pendiente';
      const esRechazado = estadoAlerta === 'rechazado';
      
      if (esPendiente || esRechazado) {
        console.error(`❌ [gastoMesActual] ERROR: Gasto ${g.id} con estado ${estadoAlerta} incluido en cálculo!`, {
          id: g.id,
          estado_alerta: g.estado_alerta,
          monto: g.monto,
          trabajador: g.trabajador
        });
        return false; // Excluir explícitamente
      }
      
      return true; // Solo incluir aprobados o null/undefined
    })
    .reduce((acc, g) => acc + g.monto, 0);

  // 📊 DISTRIBUCIÓN POR CATEGORÍA: calcular dinámicamente desde gastosFiltradosPorPeriodo (misma fuente que gastoMesActual)
  // Para trabajadores, solo mostrará sus propios gastos; para Admin/Contadora, mostrará todos
  // ✅ CORREGIDO: Usar gastosFiltradosPorPeriodo en lugar de gastosFiltrados para mantener consistencia
  // ✅ IMPORTANTE: Solo incluye gastos APROBADOS (excluye pendientes y rechazados)
  // ✅ VALIDACIÓN ADICIONAL: Verificar que ningún gasto pendiente se incluya
  const gastosPorCategoria = gastosFiltradosPorPeriodo
    .filter((gasto: any) => {
      const estadoAlerta = (gasto.estado_alerta || '').toString().toLowerCase().trim();
      const esPendiente = estadoAlerta === 'pendiente';
      const esRechazado = estadoAlerta === 'rechazado';
      
      if (esPendiente || esRechazado) {
        console.error(`❌ [gastosPorCategoria] ERROR: Gasto ${gasto.id} con estado ${estadoAlerta} incluido en cálculo!`);
        return false; // Excluir explícitamente
      }
      
      return true; // Solo incluir aprobados o null/undefined
    })
    .reduce((acc, gasto) => {
    const categoria = gasto.categoria || 'Sin categoría';
    acc[categoria] = (acc[categoria] || 0) + gasto.monto;
    return acc;
  }, {} as Record<string, number>);

  // Calcular el gasto total del mes desde gastosFiltradosPorPeriodo (para el porcentaje)
  // ✅ CORREGIDO: Usar gastoMesActual directamente en lugar de recalcular
  const gastoDelMesFiltrado = gastoMesActual;

  const distribucionPorCategoria = (Object.entries(gastosPorCategoria) as [string, number][])
    .map(([categoria, monto]) => ({
      categoria,
      monto,
      porcentaje: gastoDelMesFiltrado > 0 ? Math.round((monto / gastoDelMesFiltrado) * 100) : 0
    }))
    .sort((a, b) => b.monto - a.monto) // Ordenar por monto descendente
    .slice(0, 5); // Top 5 categorías

  // Calcular período anterior para comparación
  const obtenerRangoPeriodoAnterior = () => {
    const rangoActual = obtenerRangoPeriodo();
    const diasDiferencia = Math.ceil((rangoActual.fin.getTime() - rangoActual.inicio.getTime()) / (1000 * 60 * 60 * 24));
    
    const inicioAnterior = new Date(rangoActual.inicio);
    inicioAnterior.setDate(inicioAnterior.getDate() - diasDiferencia - 1);
    
    const finAnterior = new Date(rangoActual.inicio);
    finAnterior.setDate(finAnterior.getDate() - 1);
    finAnterior.setHours(23, 59, 59, 999);
    
    return { inicio: inicioAnterior, fin: finAnterior };
  };

  const rangoPeriodoAnterior = obtenerRangoPeriodoAnterior();
  const gastosPeriodoAnterior = excluirGastosRechazados(
    gastos.filter(gasto => {
      const fechaGasto = normalizarFecha(gasto.fecha);
      if (!fechaGasto) return false;
      return fechaGasto >= rangoPeriodoAnterior.inicio && fechaGasto <= rangoPeriodoAnterior.fin;
    })
  );

  // 💰 Gasto del Período Anterior: para comparación
  const gastoMesAnterior = gastosPeriodoAnterior
    .reduce((acc, g) => acc + g.monto, 0);

  // 📊 Calcular variación porcentual real
  const variacionPorcentual = gastoMesAnterior > 0
    ? Math.round(((gastoMesActual - gastoMesAnterior) / gastoMesAnterior) * 100)
    : 0;
  
  // 📊 Calcular promedio de últimos 3 meses (simulado - en producción vendría de histórico)
  const promedioUltimos3Meses = gastoMesAnterior > 0 
    ? Math.round((gastoMesActual + gastoMesAnterior) / 2) // Aproximación con 2 meses disponibles
    : gastoMesActual;


  // Función helper para normalizar nombres (igual que en el filtro)
  const normalizarNombre = (str: string) => {
    if (!str) return '';
    return str.toLowerCase().trim().replace(/[.\s\-_]/g, '');
  };

  // 💰 Gasto por Trabajador del Período Seleccionado
  // ✅ CORREGIDO: Normalizar nombres para que coincidan
  // ✅ IMPORTANTE: Solo incluye gastos APROBADOS (excluye pendientes y rechazados)
  // ✅ VALIDACIÓN ADICIONAL: Verificar que ningún gasto pendiente se incluya
  const gastosPorTrabajadorMes = gastosFiltradosPorPeriodo
    .filter((g: any) => {
      const estadoAlerta = (g.estado_alerta || '').toString().toLowerCase().trim();
      const esPendiente = estadoAlerta === 'pendiente';
      const esRechazado = estadoAlerta === 'rechazado';
      
      if (esPendiente || esRechazado) {
        console.error(`❌ [gastosPorTrabajadorMes] ERROR: Gasto ${g.id} con estado ${estadoAlerta} incluido en cálculo!`);
        return false; // Excluir explícitamente
      }
      
      return true; // Solo incluir aprobados o null/undefined
    })
    .reduce((acc, g) => {
      const trabajador = g.trabajador || 'Sin asignar';
      const trabajadorNormalizado = normalizarNombre(trabajador);
      acc[trabajadorNormalizado] = (acc[trabajadorNormalizado] || 0) + g.monto;
      return acc;
    }, {} as Record<string, number>);
  
  console.log('🔍 [CajaChica] gastosPorTrabajadorMes (normalizados):', gastosPorTrabajadorMes);

  // 🟢 Saldo Disponible por Trabajador
  // ✅ CORREGIDO: Normalizar nombres para buscar correctamente
  const saldoPorTrabajador = trabajadores.map(trabajador => {
    const nombreNormalizado = normalizarNombre(trabajador.nombre);
    const gastoTrabajador = gastosPorTrabajadorMes[nombreNormalizado] || 0;
    const saldo = PRESUPUESTO_MENSUAL_POR_TRABAJADOR - gastoTrabajador;
    const porcentajeUtilizado = PRESUPUESTO_MENSUAL_POR_TRABAJADOR > 0
      ? Math.round((gastoTrabajador / PRESUPUESTO_MENSUAL_POR_TRABAJADOR) * 100)
      : 0;
    
    return {
      id: trabajador.id,
      nombre: trabajador.nombre,
      email: trabajador.email,
      presupuesto: PRESUPUESTO_MENSUAL_POR_TRABAJADOR,
      gasto: gastoTrabajador,
      saldo: saldo,
      porcentajeUtilizado: porcentajeUtilizado,
      estado: saldo < 0 ? 'excedido' : saldo < (PRESUPUESTO_MENSUAL_POR_TRABAJADOR * 0.1) ? 'critico' : 'normal'
    };
  });
  
  console.log('🔍 [CajaChica] saldoPorTrabajador:', saldoPorTrabajador.map(t => ({ 
    nombre: t.nombre, 
    gasto: t.gasto,
    nombreNormalizado: normalizarNombre(t.nombre)
  })));

  // 🟢 Saldo Disponible: Si es trabajador, mostrar solo su saldo; si es admin/contadora, mostrar el total
  // ✅ FALLBACK: Si trabajadores está vacío, calcular saldo desde gastos (igual que "Gasto del Mes")
  let saldoDisponibleTotal: number;
  if (trabajadores.length === 0) {
    // Calcular saldo directamente desde presupuesto y gastos
    saldoDisponibleTotal = Math.max(0, PRESUPUESTO_MENSUAL - gastoMesActual);
  } else {
    // Cálculo normal cuando hay trabajadores
    saldoDisponibleTotal = saldoPorTrabajador.reduce((acc, t) => acc + Math.max(0, t.saldo), 0);
  }
  
  // Si es trabajador, obtener su saldo específico
  let saldoDisponible: number;
  let presupuestoMostrar: number;
  let gastoMostrar: number;
  
  if (userRole === 'Usuario') {
    // Trabajador: mostrar solo su saldo
    // ✅ CORREGIDO: Normalizar nombres para buscar correctamente
    const nombreUsuarioNormalizado = normalizarNombre(user?.nombre || '');
    const emailUsuarioNormalizado = normalizarNombre(user?.correo || '');
    
    console.log('🔍 [CajaChica] Buscando trabajadorActual:', {
      nombreUsuario: user?.nombre,
      nombreUsuarioNormalizado,
      emailUsuario: user?.correo,
      emailUsuarioNormalizado
    });
    
    const trabajadorActual = saldoPorTrabajador.find(t => {
      const nombreTrabajadorNormalizado = normalizarNombre(t.nombre);
      const emailTrabajadorNormalizado = normalizarNombre(t.email);
      return nombreTrabajadorNormalizado === nombreUsuarioNormalizado ||
             emailTrabajadorNormalizado === emailUsuarioNormalizado;
    });
    
    if (trabajadorActual) {
      saldoDisponible = Math.max(0, trabajadorActual.saldo);
      presupuestoMostrar = PRESUPUESTO_MENSUAL_POR_TRABAJADOR;
      gastoMostrar = trabajadorActual.gasto;
      console.log('✅ [CajaChica] trabajadorActual encontrado:', {
        nombre: trabajadorActual.nombre,
        gasto: trabajadorActual.gasto,
        gastoMostrar
      });
    } else {
      // Si no se encuentra el trabajador, calcular desde los gastos del usuario
      // ✅ CORREGIDO: Intentar con nombre y email normalizados, y también con la parte del nombre del email
      const nombreBuscar = normalizarNombre(user?.nombre || '');
      const emailBuscar = normalizarNombre(user?.correo || '');
      // Extraer la parte del nombre del email (antes del @) y normalizarla
      const emailSinDominio = user?.correo?.split('@')[0] || '';
      const nombreDelEmail = normalizarNombre(emailSinDominio);
      
      const gastoTrabajador = gastosPorTrabajadorMes[nombreBuscar] || 
                             gastosPorTrabajadorMes[emailBuscar] || 
                             gastosPorTrabajadorMes[nombreDelEmail] || 0;
      saldoDisponible = Math.max(0, PRESUPUESTO_MENSUAL_POR_TRABAJADOR - gastoTrabajador);
      presupuestoMostrar = PRESUPUESTO_MENSUAL_POR_TRABAJADOR;
      gastoMostrar = gastoTrabajador;
      console.log('⚠️ [CajaChica] trabajadorActual NO encontrado, usando fallback:', {
        nombreBuscar,
        emailBuscar,
        nombreDelEmail,
        gastoTrabajador,
        gastoMostrar,
        keysDisponibles: Object.keys(gastosPorTrabajadorMes)
      });
    }
  } else {
    // Administrador o Contadora: mostrar saldo total
    saldoDisponible = saldoDisponibleTotal;
    presupuestoMostrar = PRESUPUESTO_MENSUAL;
    gastoMostrar = gastoMesActual;
  }


  // Gastos Pendientes: conteo de registros no aprobados
  const pendientes = gastos.filter(g => !g.aprobado).length;


  // 📊 CÁLCULOS ADICIONALES DINÁMICOS
  
  // Gasto total del período seleccionado
  // ✅ CORREGIDO: Excluir gastos pendientes y rechazados del cálculo
  const gastoDelMes = gastosFiltradosPorPeriodo
    .filter((g: any) => {
      const estadoAlerta = (g.estado_alerta || '').toString().toLowerCase().trim();
      return estadoAlerta !== 'pendiente' && estadoAlerta !== 'rechazado';
    })
    .reduce((total, gasto) => total + gasto.monto, 0);
  
  // Calcular días del período seleccionado
  const diasDelPeriodo = Math.ceil((rangoPeriodo.fin.getTime() - rangoPeriodo.inicio.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  const diasDelMes = diasDelPeriodo;
  const gastoPromedioDiario = diasDelMes > 0 ? Math.round(gastoMesActual / diasDelMes) : 0;
  
  // Calcular día actual dentro del período
  const hoy = new Date();
  hoy.setHours(0, 0, 0, 0);
  const inicioPeriodo = new Date(rangoPeriodo.inicio);
  inicioPeriodo.setHours(0, 0, 0, 0);
  const finPeriodo = new Date(rangoPeriodo.fin);
  finPeriodo.setHours(23, 59, 59, 999);
  
  let diaActualDelPeriodo = 0;
  if (hoy >= inicioPeriodo && hoy <= finPeriodo) {
    // Si hoy está dentro del período, calcular cuántos días han pasado
    diaActualDelPeriodo = Math.ceil((hoy.getTime() - inicioPeriodo.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  } else if (hoy > finPeriodo) {
    // Si el período ya pasó, mostrar el último día
    diaActualDelPeriodo = diasDelPeriodo;
  } else {
    // Si el período aún no comienza, mostrar 0
    diaActualDelPeriodo = 0;
  }
  
  // Proyección mensual: gasto promedio diario * 30 días * 1.08 (8% de aumento estimado)
  const gastoProyectadoMes = Math.round(gastoPromedioDiario * 30 * 1.08);
  
  // Categoría con mayor gasto: calcular dinámicamente desde los gastos filtrados (ya incluye filtro de trabajador si aplica)
  const categoriaMayorGasto = (Object.entries(gastosPorCategoria) as [string, number][]).reduce((max, [categoria, monto]) => {
    return monto > (max[1] || 0) ? [categoria, monto] : max;
  }, ['Sin categoría', 0] as [string, number])[0];
  
  
  // Porcentaje de utilización del presupuesto
  // Calcular porcentaje de presupuesto según el rol del usuario
  const porcentajePresupuesto = gastoMostrar > 0 
    ? Math.round((gastoMostrar / presupuestoMostrar) * 100) 
    : 0;
  
  
  // 📊 GASTO POR TRABAJADOR: calcular dinámicamente desde el período seleccionado
  // ✅ CRÍTICO: Excluir explícitamente gastos pendientes y rechazados (defensa en profundidad)
  const gastosPorTrabajador = gastosFiltradosPorPeriodo
    .filter((g: any) => {
      const estadoAlerta = (g.estado_alerta || '').toString().toLowerCase().trim();
      const esPendiente = estadoAlerta === 'pendiente';
      const esRechazado = estadoAlerta === 'rechazado';
      
      if (esPendiente || esRechazado) {
        console.error(`❌ [gastosPorTrabajador] ERROR: Gasto ${g.id} con estado ${estadoAlerta} incluido en cálculo!`);
        return false;
      }
      return true;
    })
    .reduce((acc, gasto) => {
      const trabajador = gasto.trabajador || 'Sin asignar';
      acc[trabajador] = (acc[trabajador] || 0) + gasto.monto;
      return acc;
    }, {} as Record<string, number>);
  
  const gastoPorTrabajadorLista = (Object.entries(gastosPorTrabajador) as [string, number][])
    .map(([trabajador, gasto]) => ({
      trabajador,
      gasto,
      porcentaje: gastoMesActual > 0 ? Math.round((gasto / gastoMesActual) * 100) : 0
    }))
    .sort((a, b) => b.gasto - a.gasto) // Ordenar por gasto descendente
    .slice(0, 5); // Top 5 trabajadores
  
  // 📊 TOP TRABAJADOR (para análisis expandido)
  const topTrabajador = gastoPorTrabajadorLista.length > 0 ? gastoPorTrabajadorLista[0] : null;
  
  // 📊 TOP CATEGORÍA (para análisis expandido)
  const topCategoria = distribucionPorCategoria.length > 0 ? distribucionPorCategoria[0] : null;
  
  // 📊 DÍA CON MAYOR GASTO (para análisis expandido) - del período seleccionado
  // ✅ CRÍTICO: Excluir explícitamente gastos pendientes y rechazados (defensa en profundidad)
  const gastosPorDia = gastosFiltradosPorPeriodo
    .filter((g: any) => {
      const estadoAlerta = (g.estado_alerta || '').toString().toLowerCase().trim();
      const esPendiente = estadoAlerta === 'pendiente';
      const esRechazado = estadoAlerta === 'rechazado';
      
      if (esPendiente || esRechazado) {
        console.error(`❌ [gastosPorDia] ERROR: Gasto ${g.id} con estado ${estadoAlerta} incluido en cálculo!`);
        return false;
      }
      return true;
    })
    .reduce((acc, gasto) => {
      try {
        const fecha = new Date(gasto.fecha);
        const diaKey = fecha.toISOString().split('T')[0];
        if (!acc[diaKey]) {
          acc[diaKey] = { fecha: fecha, monto: 0, diaFormateado: fecha.toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short' }) };
        }
        acc[diaKey].monto += gasto.monto;
        return acc;
      } catch {
        return acc;
      }
    }, {} as Record<string, { fecha: Date, monto: number, diaFormateado: string }>);
  
  const diaMayorGasto = (Object.entries(gastosPorDia) as [string, { fecha: Date, monto: number, diaFormateado: string }][])
    .map(([_, data]) => ({
      ...data,
      porcentaje: gastoMesActual > 0 ? Math.round((data.monto / gastoMesActual) * 100) : 0
    }))
    .sort((a, b) => b.monto - a.monto)[0];
  
  
  // 📊 ANALÍTICA AVANZADA: calcular dinámicamente
  const gastoActual = gastoMesActual;
  const gastoProyectado = gastoProyectadoMes;
  
  // Calcular desviación estándar usando el servicio de analítica (del período seleccionado)
  const desviacionEstandar = calcularDesviacionEstandar(gastosFiltradosPorPeriodo);
  
  
  // Calcular porcentaje de variación respecto al presupuesto de la categoría con mayor gasto
  const montoCategoriaMayorGasto = gastosPorCategoria[categoriaMayorGasto] || 0;
  const porcentajeVariacionCategoria = PRESUPUESTO_MENSUAL > 0
    ? Math.round((montoCategoriaMayorGasto / PRESUPUESTO_MENSUAL) * 100)
    : 0;
  

  // 🧩 FUNCIÓN DE VALIDACIÓN AUTOMÁTICA DE WIDGETS
  // Compara los valores mostrados en la UI con los cálculos internos
  // Usar useCallback para evitar problemas de inicialización
  const validarWidgetsCajaChica = useCallback(() => {
    // Función de validación deshabilitada para reducir mensajes en consola
    // Descomentar los console.log si necesitas depurar
    /*
    console.group('🧩 [Verificación Caja Chica]');
    */
    
    let todasCorrectas = true;
    const discrepancias: string[] = [];
    const errores: string[] = [];
    
    // Helper para extraer número de un string (ej: "$40,000" -> 40000)
    const extraerNumero = (texto: string): number | null => {
      const numeroLimpio = texto.replace(/[^0-9]/g, '');
      return numeroLimpio ? parseInt(numeroLimpio, 10) : null;
    };
    
    // Helper para extraer porcentaje de un string (ej: "100%" -> 100)
    const extraerPorcentaje = (texto: string): number | null => {
      const numeroLimpio = texto.replace(/[^0-9.]/g, '');
      return numeroLimpio ? parseFloat(numeroLimpio) : null;
    };
    
    // 1. Validar Gasto del Mes Actual
    try {
      const elementoGasto = document.querySelector('[data-testid="widget-gasto-mes-actual"]');
      if (elementoGasto) {
        const valorUI = extraerNumero(elementoGasto.textContent || '');
        const valorCalculado = gastoMesActual;
        
        if (valorUI === valorCalculado) {
          // console.log(`✅ Gasto del Mes Actual: UI = ${valorUI?.toLocaleString()} | Calculado = ${valorCalculado.toLocaleString()}`);
        } else {
          todasCorrectas = false;
          discrepancias.push(`Gasto del Mes Actual: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
          // console.warn(`⚠️ Discrepancia detectada → Gasto del Mes Actual: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
        }
      } else {
        todasCorrectas = false;
        errores.push('Gasto del Mes Actual: Elemento no encontrado en DOM');
        // console.error('❌ Gasto del Mes Actual: Elemento no encontrado en DOM');
      }
    } catch (error) {
      todasCorrectas = false;
      const errorMsg = error instanceof Error ? error.message : String(error || 'Error desconocido');
      errores.push(`Gasto del Mes Actual: Error al validar - ${errorMsg}`);
      // console.error('❌ Gasto del Mes Actual: Error al validar', error);
    }
    
    // 2. Validar Saldo Disponible
    try {
      const elementoSaldo = document.querySelector('[data-testid="widget-saldo-disponible"]');
      if (elementoSaldo) {
        const valorUI = extraerNumero(elementoSaldo.textContent || '');
        const valorCalculado = saldoDisponible;
        
        if (valorUI === valorCalculado) {
          // console.log(`✅ Saldo Disponible: UI = ${valorUI?.toLocaleString()} | Calculado = ${valorCalculado.toLocaleString()}`);
        } else {
          todasCorrectas = false;
          discrepancias.push(`Saldo Disponible: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
          // console.warn(`⚠️ Discrepancia detectada → Saldo Disponible: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
        }
      } else {
        todasCorrectas = false;
        errores.push('Saldo Disponible: Elemento no encontrado en DOM');
        // console.error('❌ Saldo Disponible: Elemento no encontrado en DOM');
      }
    } catch (error) {
      todasCorrectas = false;
      const errorMsg = error instanceof Error ? error.message : String(error || 'Error desconocido');
      errores.push(`Saldo Disponible: Error al validar - ${errorMsg}`);
      // console.error('❌ Saldo Disponible: Error al validar', error);
    }
    
    // 3. Validar Categoría Mayor Gasto
    try {
      const elementoCategoria = document.querySelector('[data-testid="widget-categoria-mayor"]');
      if (elementoCategoria) {
        const textoUI = elementoCategoria.textContent || '';
        const categoriaUI = textoUI.split(':')[0];
        const porcentajeUI = extraerPorcentaje(textoUI.split('+')[1] || '');
        const porcentajeCalculado = porcentajeVariacionCategoria;
        
        if (categoriaUI.trim() === categoriaMayorGasto && 
            porcentajeUI !== null && 
            Math.abs(porcentajeUI - porcentajeCalculado) < 1) { // Tolerancia de 1%
          // console.log(`✅ Categoría Mayor: ${categoriaUI.trim()} (${porcentajeUI}%)`);
        } else {
          todasCorrectas = false;
          discrepancias.push(`Categoría Mayor: UI = ${categoriaUI.trim()} (${porcentajeUI}%) | Calculado = ${categoriaMayorGasto} (${porcentajeCalculado}%)`);
          // console.warn(`⚠️ Discrepancia detectada → Categoría Mayor: UI = ${categoriaUI.trim()} (${porcentajeUI}%) | Calculado = ${categoriaMayorGasto} (${porcentajeCalculado}%)`);
        }
      } else {
        todasCorrectas = false;
        errores.push('Categoría Mayor Gasto: Elemento no encontrado en DOM');
        // console.error('❌ Categoría Mayor Gasto: Elemento no encontrado en DOM');
      }
    } catch (error) {
      todasCorrectas = false;
      const errorMsg = error instanceof Error ? error.message : String(error || 'Error desconocido');
      errores.push(`Categoría Mayor Gasto: Error al validar - ${errorMsg}`);
      // console.error('❌ Categoría Mayor Gasto: Error al validar', error);
    }
    
    // 6. Validar Proyección Mensual
    try {
      const elementoProyeccion = document.querySelector('[data-testid="widget-proyeccion-mensual"]');
      if (elementoProyeccion) {
        const valorUI = extraerNumero(elementoProyeccion.textContent || '');
        const valorCalculado = gastoProyectadoMes;
        
        if (valorUI === valorCalculado) {
          // console.log(`✅ Proyección Mensual: UI = ${valorUI?.toLocaleString()} | Calculado = ${valorCalculado.toLocaleString()} (+8% estimado vs actual)`);
        } else {
          todasCorrectas = false;
          discrepancias.push(`Proyección Mensual: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
          // console.warn(`⚠️ Discrepancia detectada → Proyección Mensual: UI = ${valorUI?.toLocaleString() || 'N/A'} | Calculado = ${valorCalculado.toLocaleString()}`);
        }
      } else {
        todasCorrectas = false;
        errores.push('Proyección Mensual: Elemento no encontrado en DOM');
        // console.error('❌ Proyección Mensual: Elemento no encontrado en DOM');
      }
    } catch (error) {
      todasCorrectas = false;
      const errorMsg = error instanceof Error ? error.message : String(error || 'Error desconocido');
      errores.push(`Proyección Mensual: Error al validar - ${errorMsg}`);
      // console.error('❌ Proyección Mensual: Error al validar', error);
    }
    
    // 7. Validar Utilización Presupuesto
    try {
      const elementoUtilizacion = document.querySelector('[data-testid="widget-utilizacion-presupuesto"]');
      if (elementoUtilizacion) {
        const valorUI = extraerPorcentaje(elementoUtilizacion.textContent || '');
        const valorCalculado = porcentajePresupuesto;
        
        if (valorUI !== null && Math.abs(valorUI - valorCalculado) < 1) { // Tolerancia de 1%
          // console.log(`✅ Utilización Presupuesto: UI = ${valorUI}% | Calculado = ${valorCalculado}%`);
        } else {
          todasCorrectas = false;
          discrepancias.push(`Utilización Presupuesto: UI = ${valorUI}% | Calculado = ${valorCalculado}%`);
          // console.warn(`⚠️ Discrepancia detectada → Utilización Presupuesto: UI = ${valorUI}% | Calculado = ${valorCalculado}%`);
        }
      } else {
        todasCorrectas = false;
        errores.push('Utilización Presupuesto: Elemento no encontrado en DOM');
        // console.error('❌ Utilización Presupuesto: Elemento no encontrado en DOM');
      }
    } catch (error) {
      todasCorrectas = false;
      const errorMsg = error instanceof Error ? error.message : String(error || 'Error desconocido');
      errores.push(`Utilización Presupuesto: Error al validar - ${errorMsg}`);
      // console.error('❌ Utilización Presupuesto: Error al validar', error);
    }
    
    // Resumen final
    /*
    console.groupEnd();
    
    if (todasCorrectas && discrepancias.length === 0 && errores.length === 0) {
      console.log('%c🎯 Todos los widgets de Caja Chica están sincronizados correctamente con la API Mock', 
        'color: green; font-weight: bold; font-size: 14px;');
    } else {
      console.warn('%c⚠️ Algunos widgets no coinciden con los cálculos internos. Revisa la configuración o los renderizados.', 
        'color: orange; font-weight: bold; font-size: 14px;');
      
      if (discrepancias.length > 0) {
        console.group('⚠️ Discrepancias detectadas:');
        discrepancias.forEach(discrepancia => console.warn(discrepancia));
        console.groupEnd();
      }
      
      if (errores.length > 0) {
        console.group('❌ Errores detectados:');
        errores.forEach(error => console.error(error));
        console.groupEnd();
      }
    }
    */
    
    return {
      todasCorrectas,
      discrepancias,
      errores
    };
  }, [gastoMesActual, saldoDisponible, categoriaMayorGasto, porcentajeVariacionCategoria, gastoProyectadoMes, porcentajePresupuesto]);

  // Logs removidos para reducir ruido en consola

  // Cerrar dropdown cuando se actualiza el estado (solo si se completó exitosamente)
  // Este efecto se maneja manualmente en handleActualizarEstadoAlerta

  // 🧠 Detectar anomalías cuando cambian los gastos
  useEffect(() => {
    // ✅ NUEVO: Si estamos recargando por actualización de estado, no ejecutar detección
    if (recargandoPorActualizacionEstado.current) {
      console.log('⚠️ [CajaChica] Recarga automática en progreso, omitiendo detección de anomalías');
      recargandoPorActualizacionEstado.current = false; // Resetear flag
      return;
    }

    if (gastos.length > 0) {
      // ✅ CRÍTICO: NO ejecutar actualizaciones automáticas si hay una actualización manual en progreso
      // Esto evita que el botón quede bloqueado temporalmente cuando se detectan anomalías
      if (actualizandoEstado) {
        console.log('⚠️ [CajaChica] Actualización manual en progreso, omitiendo detección automática');
        return;
      }
      
      // ✅ SOLUCIÓN: Filtrar gastos que ya tienen estado_alerta aprobado o rechazado
      // Solo detectar anomalías en gastos pendientes o sin estado_alerta
      // Esto evita que las alertas aprobadas/rechazadas vuelvan a aparecer
      const gastosParaAnalizar = gastos.filter(gasto => {
        const estadoAlerta = (gasto as any).estado_alerta;
        // Excluir gastos rechazados o aprobados
        // Incluir solo gastos pendientes o sin estado_alerta (compatibilidad con gastos antiguos)
        // IMPORTANTE: Si estado_alerta es 'rechazado' o 'aprobado', NO analizar
        if (estadoAlerta === 'rechazado' || estadoAlerta === 'aprobado') {
          console.log(`🚫 [CajaChica] Excluyendo gasto ${gasto.id} con estado_alerta: ${estadoAlerta}`);
          return false;
        }
        // Incluir solo si es 'pendiente' o null/undefined (gastos antiguos sin estado)
        const incluir = !estadoAlerta || estadoAlerta === 'pendiente';
        if (incluir) {
          console.log(`✅ [CajaChica] Incluyendo gasto ${gasto.id} con estado_alerta: ${estadoAlerta || 'null/undefined'}`);
        }
        return incluir;
      });
      
      console.log(`🔍 [CajaChica] Analizando ${gastosParaAnalizar.length} de ${gastos.length} gastos (excluyendo aprobados/rechazados)`);
      console.log(`📊 [CajaChica] Estados de alerta de los gastos:`, gastos.map((g: any) => ({ id: g.id, estado_alerta: g.estado_alerta })));
      
      // ✅ CAMBIO: Detectar anomalías solo en gastos pendientes
      const anomaliasDetectadas = detectarTodasAnomalias(gastosParaAnalizar, PRESUPUESTO_MENSUAL);
      
      // ✅ NUEVO: Marcar gastos con anomalías como 'pendiente' en la BD
      const gastosConAnomalias = new Set(anomaliasDetectadas.map(a => String(a.gasto.id)));
      
      // Actualizar estados de gastos con anomalías (marcar como pendiente)
      if (anomaliasDetectadas.length > 0) {
        const promesasPendientes = Array.from(gastosConAnomalias).map(async (gastoId) => {
          try {
            const gasto = gastos.find(g => String(g.id) === gastoId);
            // Solo marcar como pendiente si no tiene estado o ya es pendiente
            // No sobrescribir estados 'aprobado' o 'rechazado' que ya fueron establecidos manualmente
            if (gasto && (!(gasto as any).estado_alerta || (gasto as any).estado_alerta === 'pendiente')) {
              await updateAlertaEstado(gastoId, 'pendiente');
              console.log(`✅ [CajaChica] Gasto ${gastoId} marcado como pendiente por anomalía detectada`);
            }
          } catch (error) {
            console.error(`❌ [CajaChica] Error al marcar gasto ${gastoId} como pendiente:`, error);
          }
        });
        
        // Ejecutar todas las actualizaciones en paralelo (no bloquear)
        Promise.allSettled(promesasPendientes).catch(() => {
          // Los errores individuales ya se manejan arriba
        });
      }
      
      // ✅ NUEVO: Marcar automáticamente como 'aprobado' los gastos sin anomalías
      const gastosSinAnomalias = gastosParaAnalizar.filter(gasto => {
        const gastoId = String(gasto.id);
        return !gastosConAnomalias.has(gastoId);
      });
      
      if (gastosSinAnomalias.length > 0) {
        const promesasAprobadas = gastosSinAnomalias.map(async (gasto) => {
          const gastoId = String(gasto.id);
          const estadoAlerta = (gasto as any).estado_alerta;
          
          // Solo aprobar automáticamente si:
          // 1. El gasto NO tiene anomalías detectadas (ya filtrado arriba)
          // 2. El gasto no tiene estado_alerta establecido (null/undefined) o es 'pendiente'
          // 3. No sobrescribir estados 'aprobado' o 'rechazado' que ya fueron establecidos manualmente
          if (!estadoAlerta || estadoAlerta === 'pendiente') {
            try {
              await updateAlertaEstado(gastoId, 'aprobado');
              console.log(`✅ [CajaChica] Gasto ${gastoId} marcado automáticamente como aprobado (sin anomalías)`);
            } catch (error) {
              console.error(`❌ [CajaChica] Error al marcar gasto ${gastoId} como aprobado:`, error);
            }
          }
        });
        
        // ✅ NUEVO: Recargar gastos después de que se completen todas las actualizaciones
        Promise.allSettled(promesasAprobadas).then(() => {
          // Esperar un poco para asegurar que la BD se actualizó
          setTimeout(async () => {
            console.log('🔄 [CajaChica] Recargando gastos después de actualizar estados de alerta...');
            recargandoPorActualizacionEstado.current = true; // Marcar que estamos recargando
            await cargarGastos();
          }, 300);
        }).catch(() => {
          // Los errores individuales ya se manejan arriba
        });
      }
      
      // ✅ IMPORTANTE: Preservar el estado de las anomalías existentes que ya fueron actualizadas
      // Si una anomalía ya tiene un estado diferente a 'pendiente', mantenerlo
      setAnomalias(prevAnomalias => {
        // ✅ MEJORADO: Usar Map con ID único de anomalía para preservar estados correctamente
        // Esto evita que múltiples anomalías del mismo tipo/gasto se confundan
        const anomaliasMapPorId = new Map(prevAnomalias.map(a => [a.id, a]));
        
        const nuevasAnomalias: Anomalia[] = anomaliasDetectadas.map(nuevaAnomalia => {
          // Buscar por ID primero (más preciso)
          const anomaliaExistentePorId = anomaliasMapPorId.get(nuevaAnomalia.id);
          
          if (anomaliaExistentePorId) {
            // ✅ MEJORADO: Verificar también el estado_alerta del gasto en la BD
            // Esto asegura que usamos el estado más reciente de la BD, no solo el estado local
            const gastoActual = gastos.find(g => String(g.id) === String(nuevaAnomalia.gasto.id));
            const estadoAlertaGasto = gastoActual ? (gastoActual as any).estado_alerta : null;
            
            // Si el gasto tiene estado_alerta aprobado/rechazado en la BD, usar ese estado (prioridad)
            if (estadoAlertaGasto === 'aprobado' || estadoAlertaGasto === 'rechazado') {
              console.log(`🔄 [CajaChica] Usando estado_alerta del gasto en BD: ${estadoAlertaGasto} (prioridad sobre estado local)`);
              return { ...nuevaAnomalia, estado: estadoAlertaGasto as 'aprobado' | 'rechazado' };
            }
            
            // Si no, preservar el estado de la anomalía existente (fallback)
            // ✅ CRÍTICO: Si existe por ID, preservar su estado (incluyendo 'aprobado' y 'rechazado')
            // Esto asegura que las alertas aprobadas manualmente no se sobrescriban
            console.log(`🔄 [CajaChica] Preservando estado de anomalía ${nuevaAnomalia.id}: ${anomaliaExistentePorId.estado}`);
            return { ...nuevaAnomalia, estado: anomaliaExistentePorId.estado };
          }
          
          // Si no existe por ID, buscar por tipo y gasto (fallback para compatibilidad)
          const anomaliaExistentePorClave = prevAnomalias.find(a => 
            a.tipo === nuevaAnomalia.tipo && 
            String(a.gasto.id) === String(nuevaAnomalia.gasto.id) &&
            (a.estado === 'aprobado' || a.estado === 'rechazado')
          );
          
          if (anomaliaExistentePorClave) {
            // ✅ CRÍTICO: Preservar el estado de la anomalía existente (aprobado/rechazado)
            console.log(`🔄 [CajaChica] Preservando estado de anomalía por clave ${nuevaAnomalia.tipo}_${nuevaAnomalia.gasto.id}: ${anomaliaExistentePorClave.estado}`);
            return { ...nuevaAnomalia, estado: anomaliaExistentePorClave.estado as 'pendiente' | 'aprobado' | 'rechazado' };
          }
          
          // Si es nueva o estaba pendiente, usar 'pendiente'
          return { ...nuevaAnomalia, estado: 'pendiente' as const };
        });
        
        // ✅ NUEVO: Mantener anomalías que ya fueron aprobadas o rechazadas
        // pero que ya no se detectan (porque el gasto fue aprobado/rechazado y no se analiza)
        // Estas se mantienen en el estado para referencia histórica, pero no se muestran
        const anomaliasAprobadasRechazadas = prevAnomalias.filter(a => {
          const estado = a.estado;
          // Mantener solo las que están aprobadas o rechazadas
          // Y que NO están en las nuevas anomalías detectadas (evitar duplicados)
          const existeEnNuevas = nuevasAnomalias.some(na => na.id === a.id);
          const mantener = (estado === 'aprobado' || estado === 'rechazado') && !existeEnNuevas;
          if (mantener) {
            console.log(`🔄 [CajaChica] Manteniendo anomalía aprobada/rechazada que ya no se detecta: ${a.id} (${estado})`);
          }
          return mantener;
        });
        
        // Combinar nuevas anomalías con las aprobadas/rechazadas existentes
        const todasLasAnomalias = [...nuevasAnomalias, ...anomaliasAprobadasRechazadas];
        
        // Solo actualizar si las anomalías realmente cambiaron (comparar por keys y estados)
        const nuevasKeys = todasLasAnomalias.map(a => `${a.id}_${a.estado}`).sort().join(',');
        const prevKeys = prevAnomalias.map(a => `${a.id}_${a.estado}`).sort().join(',');
        
        if (nuevasKeys === prevKeys && todasLasAnomalias.length === prevAnomalias.length) {
          // Las anomalías no cambiaron, no actualizar para evitar loop infinito
          return prevAnomalias;
        }
        
        console.log(`✅ [CajaChica] Anomalías actualizadas: ${nuevasAnomalias.length} nuevas, ${anomaliasAprobadasRechazadas.length} aprobadas/rechazadas mantenidas`);
        
        return todasLasAnomalias;
      });
      
      // Generar predicción solo con gastos del período seleccionado para mayor precisión
      const prediccionGenerada = generarPrediccion(
        gastosFiltradosPorPeriodo.length > 0 ? gastosFiltradosPorPeriodo : gastos,
        PRESUPUESTO_MENSUAL // Pasar el presupuesto como límite máximo
      );
      setPrediccion(prediccionGenerada);
    }
  }, [gastos, periodoFiltro, fechaInicioPersonalizada, fechaFinPersonalizada, PRESUPUESTO_MENSUAL, actualizandoEstado]); // ✅ Agregado actualizandoEstado para evitar conflictos con actualizaciones manuales

  // Verificar duplicados en tiempo real cuando cambian los campos del formulario (solo para administradores)
  useEffect(() => {
    if (!isAdministrador) {
      setGastoDuplicadoDetectado(false);
      return;
    }

    if (!nuevoGasto.fecha || !nuevoGasto.descripcion || !nuevoGasto.monto) {
      setGastoDuplicadoDetectado(false);
      return;
    }

    const trabajadorParaValidar = userRole === 'Usuario' && !nuevoGasto.trabajador
      ? (user?.nombre || user?.correo?.split('@')[0] || '')
      : nuevoGasto.trabajador;

    if (!trabajadorParaValidar) {
      setGastoDuplicadoDetectado(false);
      return;
    }

    const duplicado = gastos.find(g => 
      g.descripcion === nuevoGasto.descripcion && 
      g.monto === desformatearMonto(nuevoGasto.monto) && 
      g.fecha === nuevoGasto.fecha
    );

    setGastoDuplicadoDetectado(!!duplicado);
  }, [nuevoGasto, gastos, isAdministrador, userRole, user]);

  // 🧩 Ejecutar validación cuando cambian los gastos (los cálculos derivados se actualizarán automáticamente)
  // Usar useRef para evitar loop infinito con useCallback
  const validarWidgetsRef = useRef(validarWidgetsCajaChica);
  useEffect(() => {
    validarWidgetsRef.current = validarWidgetsCajaChica;
  }, [validarWidgetsCajaChica]);

  useEffect(() => {
    // Solo validar si hay gastos cargados (evitar validar en el primer render vacío)
    if (gastos.length > 0) {
      // Esperar a que el componente se haya renderizado completamente
      const timer = setTimeout(() => {
        validarWidgetsRef.current();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [gastos]); // Solo dependemos de gastos, no de la función

  // Verificar permisos después de todos los hooks
  if (!hasPermission('view_caja_chica')) {
    return <AccessDenied 
      message="No tienes permisos para acceder a la gestión de caja chica."
      requiredRole="Usuario, Contadora o Administrador"
      currentRole={user?.rol}
    />;
  }

  const handleFiltroChange = (campo: string, valor: string | boolean) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  // Buscar dónde comienza el return principal

  const handleAgregarGasto = async () => {
    // Si es trabajador, asegurar que el trabajador se establezca automáticamente antes de validar
    const gastoParaValidar = { ...nuevoGasto };
    if (userRole === 'Usuario' && !gastoParaValidar.trabajador) {
      // Establecer el trabajador automáticamente si no está definido
      gastoParaValidar.trabajador = user?.nombre || user?.correo?.split('@')[0] || '';
    }
    
    if (!validarGasto(gastoParaValidar)) return;
    
    setCargando(true);
    
    try {
      // Generar nombre de archivo y subir imagen a Supabase Storage si existe
      let documentoUrl: string | undefined = undefined;
      if (imagenBoleta) {
        try {
          console.log('📤 [CajaChica] Subiendo imagen a Supabase Storage...');
          console.log('   📋 Detalles del archivo:', {
            nombre: imagenBoleta.name,
            tamaño: imagenBoleta.size,
            tipo: imagenBoleta.type
          });
          
          // Verificar sesión del usuario antes de subir
          const { data: { session }, error: sessionError } = await supabase.auth.getSession();
          if (sessionError || !session) {
            console.error('❌ [CajaChica] No hay sesión activa:', sessionError);
            mostrarToast('⚠️ No hay sesión activa. Por favor, inicia sesión nuevamente.', 'error');
            setCargando(false);
            return;
          }
          console.log('✅ [CajaChica] Sesión verificada, usuario:', session.user.email);
          
          const timestamp = Date.now();
          const extension = imagenBoleta.name.split('.').pop();
          const nombreArchivo = `boleta_${timestamp}.${extension}`;
          console.log('📝 [CajaChica] Nombre de archivo generado:', nombreArchivo);
          
          // Asegurar que el tipo MIME sea correcto
          // Si el archivo no tiene tipo MIME, intentar detectarlo desde la extensión
          let tipoMime = imagenBoleta.type;
          if (!tipoMime || tipoMime === 'application/json' || tipoMime === 'application/octet-stream') {
            // Detectar tipo MIME desde la extensión
            const mimeTypes: { [key: string]: string } = {
              'png': 'image/png',
              'jpg': 'image/jpeg',
              'jpeg': 'image/jpeg',
              'gif': 'image/gif',
              'webp': 'image/webp',
              'pdf': 'application/pdf'
            };
            tipoMime = mimeTypes[extension?.toLowerCase() || ''] || 'image/png';
            console.log('⚠️ [CajaChica] Tipo MIME no detectado, usando:', tipoMime);
          }
          
          console.log('🔄 [CajaChica] Iniciando subida a bucket "comprobantes"...');
          console.log('   📋 Tipo MIME del archivo original:', imagenBoleta.type);
          console.log('   📋 Tipo MIME a usar:', tipoMime);
          const tipoObjeto = imagenBoleta instanceof File ? 'File' : typeof imagenBoleta;
          console.log('   📋 Tipo del objeto:', tipoObjeto);
          
          // Leer el archivo como ArrayBuffer para asegurar que se pase correctamente
          const arrayBuffer = await imagenBoleta.arrayBuffer();
          const blob = new Blob([arrayBuffer], { type: tipoMime });
          
          console.log('   📋 Blob creado con tipo MIME:', blob.type);
          console.log('   📋 Tamaño del Blob:', blob.size);
          
          // Intentar subir directamente - si el bucket no existe, el error lo indicará
          const { data: uploadData, error: uploadError } = await supabase.storage
            .from('comprobantes')
            .upload(nombreArchivo, blob, {
              cacheControl: '3600',
              upsert: false, // No sobrescribir si existe
              contentType: tipoMime // Especificar el tipo MIME explícitamente
            });
          
          if (uploadError) {
            console.error('❌ [CajaChica] Error al subir imagen a Supabase Storage:', {
              error: uploadError,
              message: uploadError.message,
              name: uploadError.name
            });
            // Mostrar error más específico
            let mensajeError = '⚠️ El gasto se guardó pero la imagen no se pudo subir.';
            if (uploadError.message?.includes('new row violates row-level security')) {
              mensajeError = '❌ Error de permisos: No tienes permisos para subir archivos. Contacta al administrador.';
            } else if (uploadError.message?.includes('Bucket not found')) {
              mensajeError = '❌ Error: El bucket no existe. Contacta al administrador.';
            }
            mostrarToast(mensajeError, 'error');
          } else if (uploadData) {
            console.log('✅ [CajaChica] Imagen subida exitosamente a Supabase Storage:', {
              path: uploadData.path,
              fullPath: uploadData.fullPath,
              id: uploadData.id
            });
            // Usar el path completo del archivo subido (puede incluir el nombre del archivo)
            // Si uploadData.path incluye el nombre completo, usarlo; si no, usar solo nombreArchivo
            documentoUrl = uploadData.path || nombreArchivo;
            console.log('📝 [CajaChica] Nombre de archivo a guardar en BD:', documentoUrl);
          } else {
            console.warn('⚠️ [CajaChica] No se recibió data ni error, pero la subida puede haber fallado');
          }
        } catch (error: any) {
          console.error('❌ [CajaChica] Excepción al subir imagen:', {
            error,
            message: error?.message,
            stack: error?.stack
          });
          mostrarToast('⚠️ El gasto se guardó pero la imagen no se pudo subir. Intenta subirla manualmente.', 'warning');
        }
      }
      
      // Preparar datos del gasto para Supabase
      // Si es trabajador, asegurar que el trabajador sea el usuario actual
      const trabajadorFinal = userRole === 'Usuario' 
        ? (user?.nombre || user?.correo?.split('@')[0] || gastoParaValidar.trabajador)
        : nuevoGasto.trabajador;
      
      // ✅ CRÍTICO: Asegurar que documentoUrl se asigne ANTES de crear gastoData
      // Si documentoUrl es undefined pero hay imagen, significa que la subida falló
      if (imagenBoleta && !documentoUrl) {
        console.error('❌ [CajaChica] CRÍTICO: Hay imagen pero documentoUrl es undefined. La subida del archivo falló.');
        mostrarToast('❌ Error: No se pudo subir la imagen. El gasto se guardará sin comprobante.', 'error');
      }
      
      const gastoData = {
        fecha: nuevoGasto.fecha,
        trabajador: trabajadorFinal,
        descripcion: nuevoGasto.descripcion,
        monto: desformatearMonto(nuevoGasto.monto), // Convertir monto formateado a número
        categoria: nuevoGasto.categoria,
        // ✅ CRÍTICO: Usar documentoUrl si existe, de lo contrario null (no undefined)
        documento: documentoUrl ? documentoUrl : (nuevoGasto.documento || null),
        monto_ocr: nuevoGasto.monto_ocr // ✅ NUEVO: Incluir monto_ocr si existe
      };
      
      // ✅ MEJORADO: Log para verificar que el documento y monto_ocr se están guardando
      console.log('📋 [CajaChica] Datos del gasto a guardar:', {
        documento: gastoData.documento,
        tieneDocumento: !!gastoData.documento,
        documentoUrl,
        tieneImagenBoleta: !!imagenBoleta,
        tipoDocumento: typeof gastoData.documento,
        documentoEsNull: gastoData.documento === null,
        documentoEsUndefined: gastoData.documento === undefined,
        // ✅ NUEVO: Logs para monto y monto_ocr
        monto: gastoData.monto,
        monto_ocr: gastoData.monto_ocr,
        tiene_monto_ocr: gastoData.monto_ocr !== null && gastoData.monto_ocr !== undefined,
        diferencia_montos: gastoData.monto_ocr ? Math.abs(gastoData.monto - gastoData.monto_ocr) : 'N/A',
        tipo_monto: typeof gastoData.monto,
        tipo_monto_ocr: typeof gastoData.monto_ocr
      });
      
      // ✅ VERIFICACIÓN CRÍTICA: Asegurar que documentoUrl se asigne correctamente
      if (documentoUrl && !gastoData.documento) {
        console.warn('⚠️ [CajaChica] documentoUrl existe pero no se asignó a gastoData.documento');
        gastoData.documento = documentoUrl;
        console.log('✅ [CajaChica] documentoUrl asignado a gastoData.documento:', gastoData.documento);
      }
      
      // Guardar gasto con fallback offline automático
      const resultado = await createGastoWithOfflineFallback(gastoData, imagenBoleta || undefined);
      
      if (resultado.success && resultado.data) {
        // Resetear estado de carga ANTES de cualquier otra operación
        setCargando(false);
        
        // Limpiar formulario primero
        setNuevoGasto({
          fecha: '',
          trabajador: '',
          descripcion: '',
          monto: '',
          categoria: '',
          documento: null,
          monto_ocr: undefined
        });
        setImagenBoleta(null);
        
        // Cerrar modal
        handleCloseModal();
        
        // Verificar si se guardó offline
        const guardadoOffline = !resultado.data.sincronizado;
        
        if (guardadoOffline) {
          // Mostrar mensaje de guardado offline
          mostrarToast('💾 Guardado sin conexión. Se sincronizará automáticamente.', 'success');
        } else {
          // Mostrar mensaje de éxito normal
          mostrarToast('Gasto agregado exitosamente y guardado en la base de datos', 'success');
          
          // Registrar log de auditoría de forma asíncrona (no bloquear)
          // IMPORTANTE: Usar la fecha del gasto con la hora actual del momento de registro
          // Esto asegura que cada gasto tenga un timestamp único
          const detalleGasto = `Gasto registrado: ${nuevoGasto.descripcion} - Monto: $${nuevoGasto.monto} - Categoría: ${nuevoGasto.categoria}`;
          const gastoId = resultado.data?.id?.toString() || 'nuevo';
          // Usar la fecha del gasto pero con la hora actual del momento de registro
          // Esto garantiza timestamps únicos para cada gasto
          const fechaGasto = nuevoGasto.fecha ? (() => {
            try {
              const fechaBase = new Date(nuevoGasto.fecha);
              const ahora = new Date();
              // Combinar la fecha del gasto con la hora actual del registro
              fechaBase.setHours(ahora.getHours(), ahora.getMinutes(), ahora.getSeconds(), ahora.getMilliseconds());
              return fechaBase.toISOString();
            } catch (e) {
              // Si hay error al parsear, usar fecha actual
              return new Date().toISOString();
            }
          })() : new Date().toISOString();
          registrarLog('CREAR', detalleGasto, 'gasto', gastoId, fechaGasto).catch(err => {
            console.error('Error al registrar log (no crítico):', err);
          });
        }
        
        // ✅ MEJORADO: Agregar TODOS los gastos creados inmediatamente al estado
        // No solo los pendientes, sino también los aprobados para que aparezcan de inmediato
        // Esto asegura que todos los gastos se visualicen instantáneamente sin depender de la recarga
        const gastoCreado = resultado.data;
        if (gastoCreado) {
          console.log('✅ [CajaChica] Gasto creado, agregando directamente al estado para mostrar inmediatamente:', {
            id: gastoCreado.id,
            trabajador: gastoCreado.trabajador,
            estado_alerta: gastoCreado.estado_alerta,
            fecha: gastoCreado.fecha,
            monto: gastoCreado.monto,
            rangoPeriodo: {
              inicio: rangoPeriodo.inicio.toISOString(),
              fin: rangoPeriodo.fin.toISOString()
            }
          });
          
          setGastos(prevGastos => {
            // Verificar que no esté ya en la lista (evitar duplicados)
            const existe = prevGastos.some(g => g.id === gastoCreado.id);
            if (!existe) {
              const fechaGastoNormalizada = normalizarFecha(gastoCreado.fecha);
              const dentroDelPeriodo = fechaGastoNormalizada ? 
                (fechaGastoNormalizada >= rangoPeriodo.inicio && fechaGastoNormalizada <= rangoPeriodo.fin) : false;
              
              console.log('✅ [CajaChica] Agregando gasto al estado:', {
                id: gastoCreado.id,
                estado_alerta: gastoCreado.estado_alerta,
                dentroDelPeriodo,
                fechaGasto: gastoCreado.fecha,
                fechaNormalizada: fechaGastoNormalizada?.toISOString()
              });
              
              // ✅ CRÍTICO: Marcar gasto con fecha de creación local para preservarlo en recargas
              return [...prevGastos, {
                ...gastoCreado,
                fechaCreacionLocal: new Date().toISOString() // Marcar como agregado localmente
              }];
            } else {
              console.log('⚠️ [CajaChica] Gasto ya existe en el estado, no se duplicará');
            }
            return prevGastos;
          });
          
          // ✅ NUEVO: Si es pendiente, agregar log adicional para depuración del panel de pendientes
          if (gastoCreado.estado_alerta === 'pendiente') {
            console.log('🔍 [CajaChica] Gasto pendiente agregado, verificando panel de pendientes...');
            // Forzar que React procese el estado inmediatamente
            setTimeout(() => {
              console.log('🔍 [CajaChica] Estado actualizado, el panel debería mostrar el gasto ahora');
            }, 0);
          }
        }
        
        // Recargar gastos desde la base de datos para asegurar que se muestre el nuevo gasto
        // Esto es importante porque las políticas RLS pueden filtrar los gastos
        // ✅ REDUCIDO: Delay reducido de 500ms a 300ms para actualización más rápida
        setTimeout(async () => {
          await cargarGastos();
          // Ejecutar validación automática después de recargar
          validarWidgetsCajaChica();
        }, 300);
      } else {
        // Si falla completamente (ni online ni offline), mostrar error detallado
        console.error('❌ Error al guardar gasto:', resultado);
        console.error('   Mensaje:', resultado.message);
        console.error('   Errores:', resultado.errors);
        
        const mensajeError = resultado.message || 'Error desconocido al guardar el gasto';
        
        // Mostrar mensaje de error más descriptivo
        let mensajeUsuario = `Error al guardar el gasto: ${mensajeError}`;
        
        if (mensajeError.includes('Variables de entorno')) {
          mensajeUsuario = 'Error: Supabase no está configurado. Verifica las variables de entorno REACT_APP_SUPABASE_URL y REACT_APP_SUPABASE_ANON_KEY en tu archivo .env';
        } else if (mensajeError.includes('relation "gastos" does not exist')) {
          mensajeUsuario = 'Error: La tabla "gastos" no existe en Supabase. Ejecuta el script de creación de tablas.';
        } else if (mensajeError.includes('permission denied') || mensajeError.includes('RLS')) {
          mensajeUsuario = 'Error: Permisos insuficientes. Verifica las políticas de Row Level Security (RLS) en Supabase.';
        }
        
        mostrarToast(mensajeUsuario, 'error');
      }
    } catch (error: any) {
      console.error('Error inesperado al agregar gasto:', error);
      console.error('Detalles del error:', error);
      
      let mensajeError = 'Error inesperado al agregar gasto';
      if (error?.message?.includes('Timeout')) {
        mensajeError = 'La operación tardó demasiado. Por favor, intenta nuevamente.';
      } else if (error?.message) {
        mensajeError = error.message;
      }
      
      mostrarToast(mensajeError, 'error');
    } finally {
      // Asegurar que siempre se resetee el estado de carga, incluso si hay errores
      setCargando(false);
    }
  };

  // Manejar carga de imagen
  const handleImageUpload = (file: File) => {
    setImagenBoleta(file);
  };

  // Manejar resultado del OCR y prellenar formulario
  const handleOCRComplete = (result: OCRResult) => {
    if (result.extractedData) {
      const updates: Partial<typeof nuevoGasto> = {};
      
      if (result.extractedData.monto && !nuevoGasto.monto) {
        updates.monto = result.extractedData.monto.toString();
      }
      
      // ✅ NUEVO: Guardar monto_ocr siempre que se detecte un monto en el OCR
      if (result.extractedData.monto) {
        updates.monto_ocr = result.extractedData.monto;
      }
      
      if (result.extractedData.fecha && !nuevoGasto.fecha) {
        // Asegurar que la fecha esté en formato YYYY-MM-DD
        const fechaExtraida = result.extractedData.fecha;
        // Si la fecha no está en formato YYYY-MM-DD, intentar convertirla
        if (!/^\d{4}-\d{2}-\d{2}$/.test(fechaExtraida)) {
          try {
            // Intentar parsear la fecha y convertirla a formato ISO
            const fechaParsed = new Date(fechaExtraida);
            if (!isNaN(fechaParsed.getTime())) {
              updates.fecha = fechaParsed.toISOString().split('T')[0];
            } else {
              console.warn('Fecha extraída no válida:', fechaExtraida);
            }
          } catch (e) {
            console.warn('Error al convertir fecha:', fechaExtraida, e);
          }
        } else {
          updates.fecha = fechaExtraida;
        }
      }
      
      if (result.extractedData.descripcion && !nuevoGasto.descripcion) {
        updates.descripcion = result.extractedData.descripcion;
      }
      
      if (result.extractedData.categoria && !nuevoGasto.categoria) {
        updates.categoria = result.extractedData.categoria;
      }
      
      if (Object.keys(updates).length > 0) {
        setNuevoGasto({ ...nuevoGasto, ...updates });
        // Notificación eliminada: el formulario se prellena automáticamente sin mostrar notificación
      }
    }
  };

  // Manejar eliminación de imagen
  const handleImageRemove = () => {
    setImagenBoleta(null);
  };

  // Cerrar modal y limpiar estado
  const handleCloseModal = () => {
    setMostrarModal(false);
    setNuevoGasto({
      fecha: '',
      trabajador: '',
      descripcion: '',
      monto: '',
      categoria: '',
      documento: null,
      monto_ocr: undefined
    });
    setImagenBoleta(null);
    setGastoDuplicadoDetectado(false);
  };


  const handleVerDetalle = (gasto: Gasto) => {
    setGastoSeleccionado(gasto);
    setMostrarDrawer(true);
  };

  // Función para abrir modal de detalle desde alerta
  const handleVerDetalleAlerta = (anomalia: Anomalia) => {
    // Convertir el tipo Gasto de cajaChicaAnalytics al tipo Gasto de mockApi
    // Convertir monto_ocr de forma segura
    let montoOcr: number | undefined = undefined;
    if (anomalia.gasto.monto_ocr !== null && anomalia.gasto.monto_ocr !== undefined) {
      if (typeof anomalia.gasto.monto_ocr === 'number') {
        montoOcr = anomalia.gasto.monto_ocr;
      } else if (typeof anomalia.gasto.monto_ocr === 'string') {
        const parsed = Number(anomalia.gasto.monto_ocr);
        if (!isNaN(parsed)) {
          montoOcr = parsed;
        }
      }
    }
    
    // ✅ MEJORADO: Convertir documento a string si es un objeto
    let documentoFinal: string | null = null;
    if (anomalia.gasto.documento) {
      if (typeof anomalia.gasto.documento === 'string') {
        documentoFinal = anomalia.gasto.documento.trim() || null;
      } else if (typeof anomalia.gasto.documento === 'object') {
        const docObj = anomalia.gasto.documento as any;
        if (docObj.path) {
          documentoFinal = String(docObj.path).trim() || null;
        } else if (docObj.url) {
          documentoFinal = String(docObj.url).trim() || null;
        } else if (docObj.publicUrl) {
          documentoFinal = String(docObj.publicUrl).trim() || null;
        } else {
          console.warn('⚠️ [CajaChica] Campo documento en anomalía es un objeto sin propiedades conocidas:', anomalia.gasto.documento);
          documentoFinal = null;
        }
      }
    }
    
    const gastoConvertido: Gasto = {
      id: String(anomalia.gasto.id),
      fecha: anomalia.gasto.fecha,
      monto: anomalia.gasto.monto,
      descripcion: anomalia.gasto.descripcion,
      categoria: anomalia.gasto.categoria,
      trabajador: anomalia.gasto.trabajador || '',
      documento: documentoFinal,
      sincronizado: anomalia.gasto.sincronizado || false,
      fechaSincronizacion: anomalia.gasto.fechaSincronizacion,
      aprobado: anomalia.gasto.aprobado,
      monto_ocr: montoOcr,
      estado_alerta: anomalia.gasto.estado_alerta || 'pendiente' // ✅ Incluir estado_alerta
    };
    setGastoSeleccionadoModal(gastoConvertido);
    setGastoModalAbierto(true);
  };

  // Función para actualizar estado de alerta
  const handleActualizarEstadoAlerta = async (
    anomaliaId: string, 
    gastoId: string, 
    nuevoEstado: 'pendiente' | 'aprobado' | 'rechazado'
  ) => {
    // ✅ Buscar el gasto real en la lista de gastos cargados para obtener el UUID correcto
    const anomalia = anomalias.find(a => a.id === anomaliaId);
    if (!anomalia) {
      console.error(`❌ [handleActualizarEstadoAlerta] No se encontró la anomalía con ID: ${anomaliaId}`);
      mostrarToast(`Error: No se pudo encontrar la alerta`, 'error');
      return;
    }
    
    // Buscar el gasto real usando el gasto de la anomalía
    // Intentar primero por ID, luego por descripción, monto y fecha
    const gastoReal = gastos.find(g => 
      String(g.id) === String(anomalia.gasto.id) ||
      (g.descripcion === anomalia.gasto.descripcion && 
       g.monto === anomalia.gasto.monto && 
       g.fecha === anomalia.gasto.fecha)
    );
    
    if (!gastoReal || !gastoReal.id) {
      console.error(`❌ [handleActualizarEstadoAlerta] No se encontró el gasto real para la anomalía ${anomaliaId}`);
      console.error('   Gasto de anomalía:', anomalia.gasto);
      console.error('   Gastos disponibles:', gastos.map(g => ({ id: g.id, descripcion: g.descripcion })));
      mostrarToast(`Error: No se pudo encontrar el gasto asociado`, 'error');
      return;
    }
    
    // Usar el UUID real del gasto
    const gastoIdReal = String(gastoReal.id);
    
    // Validar que el ID sea un UUID válido
    const esUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(gastoIdReal);
    if (!esUUID && !gastoIdReal.startsWith('pending_') && !gastoIdReal.startsWith('temp_')) {
      console.error(`❌ [handleActualizarEstadoAlerta] El ID del gasto no es un UUID válido: ${gastoIdReal}`);
      mostrarToast(`Error: ID de gasto inválido`, 'error');
      return;
    }
    
    // Usar clave estable basada en tipo y gasto.id real
    const claveEstable = `${anomalia.tipo}_${gastoIdReal}`;
    
    // Prevenir múltiples actualizaciones simultáneas
    if (actualizandoEstado === claveEstable) {
      console.log('⚠️ [handleActualizarEstadoAlerta] Ya se está actualizando esta alerta');
      return;
    }

    // Guardar el estado anterior para poder revertir si falla
    const estadoAnterior = anomalia.estado || 'pendiente';
    
    console.log(`🔄 [handleActualizarEstadoAlerta] Actualizando alerta ${anomaliaId} de "${estadoAnterior}" a "${nuevoEstado}"`);
    console.log(`🔍 [handleActualizarEstadoAlerta] ID original: ${gastoId}, ID real: ${gastoIdReal}`);
    
    setActualizandoEstado(claveEstable);
    
      try {
        // ✅ ACTUALIZAR: Cuando se rechaza o aprueba una alerta, actualizar el estado_alerta del gasto en la BD
        // Esto asegura que los gastos rechazados NO se sumen a los totales
        // IMPORTANTE: Aunque un gasto pueda tener múltiples anomalías, si se rechaza una alerta,
        // el gasto completo debe ser excluido de los cálculos (no se suma a los totales)
        
        if (nuevoEstado === 'rechazado' || nuevoEstado === 'aprobado') {
          console.log(`🔄 [handleActualizarEstadoAlerta] Actualizando estado_alerta del gasto ${gastoIdReal} a: ${nuevoEstado}`);
          
          try {
            // Llamar al backend para actualizar el estado_alerta del gasto en la BD
            const resultado = await updateAlertaEstado(gastoIdReal, nuevoEstado);
            
            if (!resultado.success) {
              throw new Error(resultado.message || 'Error al actualizar estado de alerta');
            }
            
            console.log(`✅ [handleActualizarEstadoAlerta] Estado de alerta del gasto actualizado en BD:`, resultado.data);
            
            // ✅ CRÍTICO: Establecer flag ANTES de recargar para evitar que useEffect interfiera
            recargandoPorActualizacionEstado.current = true;
            
            // Pequeño delay para asegurar que la actualización se haya propagado en Supabase
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Recargar los gastos para que se refleje el cambio en los cálculos
            await cargarGastos();
            
            // ✅ CRÍTICO: Esperar más tiempo para que React procese el setAnomalias y el useEffect no interfiera
            await new Promise(resolve => setTimeout(resolve, 1000));
            
            // Resetear flag DESPUÉS de que todo se haya procesado
            recargandoPorActualizacionEstado.current = false;
            
            // Verificar que la actualización fue exitosa consultando directamente desde Supabase
            // Esto nos da confianza de que el cambio se guardó en la BD
            try {
              const { data: gastoVerificado, error: errorVerificacion } = await supabase
                .from('gastos')
                .select('id, estado_alerta')
                .eq('id', gastoIdReal)
                .single();
              
              if (errorVerificacion) {
                console.error(`❌ [handleActualizarEstadoAlerta] Error al verificar el estado:`, errorVerificacion);
              } else if (gastoVerificado) {
                const estadoEnBD = gastoVerificado.estado_alerta;
                console.log(`🔍 [handleActualizarEstadoAlerta] Verificación en BD: Gasto ${gastoIdReal} tiene estado_alerta: ${estadoEnBD}`);
                
                if (estadoEnBD !== nuevoEstado) {
                  console.error(`❌ [handleActualizarEstadoAlerta] CRÍTICO: El estado en BD no coincide. Esperado: ${nuevoEstado}, Actual: ${estadoEnBD}`);
                  mostrarToast(`Error: El estado no se actualizó correctamente en la base de datos`, 'error');
                } else {
                  console.log(`✅ [handleActualizarEstadoAlerta] Verificación exitosa: El gasto ${gastoIdReal} está ${nuevoEstado} en la BD`);
                }
              }
            } catch (verificacionError: any) {
              console.error(`❌ [handleActualizarEstadoAlerta] Error en verificación:`, verificacionError);
            }
            
            console.log(`✅ [handleActualizarEstadoAlerta] Gastos recargados, el gasto ${gastoIdReal} ahora está ${nuevoEstado}`);
          } catch (updateError: any) {
            console.error(`❌ [handleActualizarEstadoAlerta] Error al actualizar en BD:`, updateError);
            throw updateError; // Re-lanzar para que se maneje en el catch principal
          }
        }
        
        // Actualizar el estado local de la anomalía ANTES de recargar gastos
        // Esto asegura que el estado se preserve cuando se ejecute el useEffect de detección
        setAnomalias(prevAnomalias => {
          let actualizada = false;
          const nuevasAnomalias = prevAnomalias.map(anomaliaItem => {
            if (anomaliaItem.id === anomaliaId) {
              actualizada = true;
              console.log(`✅ [handleActualizarEstadoAlerta] Anomalía ${anomaliaId} actualizada a: ${nuevoEstado}`);
              return { ...anomaliaItem, estado: nuevoEstado };
            }
            return anomaliaItem;
          });
          
          if (!actualizada) {
            console.warn(`⚠️ [handleActualizarEstadoAlerta] No se encontró la anomalía ${anomaliaId} para actualizar`);
            // Si no se encontró, agregarla con el nuevo estado (por si acaso)
            nuevasAnomalias.push({ ...anomalia, estado: nuevoEstado });
          }
          
          return nuevasAnomalias;
        });
        
        const mensajeToast = nuevoEstado === 'rechazado' 
          ? 'Alerta rechazada. El gasto no se sumará a los totales.' 
          : nuevoEstado === 'aprobado'
          ? 'Alerta aprobada. El gasto se incluirá en los totales.'
          : `Estado de alerta actualizado a: ${nuevoEstado}`;
        
        console.log(`✅ [handleActualizarEstadoAlerta] Estado local actualizado exitosamente a: ${nuevoEstado}`);
        mostrarToast(mensajeToast, 'success');
    } catch (error: any) {
      console.error('❌ [handleActualizarEstadoAlerta] Error al actualizar estado de alerta:', error);
      console.error('   Detalles del error:', {
        message: error.message,
        stack: error.stack,
        gastoId,
        nuevoEstado
      });
      
      // Revertir el cambio local si falla
      setAnomalias(prevAnomalias => 
        prevAnomalias.map(anomalia => {
          if (anomalia.id === anomaliaId) {
            return { ...anomalia, estado: estadoAnterior };
          }
          return anomalia;
        })
      );
      
      mostrarToast(`Error al actualizar estado de alerta: ${error.message || 'Error desconocido'}`, 'error');
    } finally {
      // ✅ CRÍTICO: Limpiar SIEMPRE, usar setTimeout para asegurar que se limpie después del re-render
      // Esto evita que el botón quede bloqueado si hay actualizaciones automáticas en curso
      setTimeout(() => {
        setActualizandoEstado(null);
      }, 100);
    }
  };

  // Funciones de utilidad
  const mostrarToast = (mensaje: any, tipo: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    // Asegurar que mensaje sea siempre un string
    let mensajeString: string;
    if (typeof mensaje === 'string') {
      mensajeString = mensaje;
    } else if (mensaje instanceof Error) {
      mensajeString = mensaje.message;
    } else if (mensaje && typeof mensaje === 'object') {
      try {
        mensajeString = JSON.stringify(mensaje);
      } catch (e) {
        mensajeString = String(mensaje || 'Error desconocido');
      }
    } else {
      mensajeString = String(mensaje || 'Mensaje desconocido');
    }
    
    setToast({ mostrar: true, mensaje: mensajeString, tipo });
    setTimeout(() => setToast({ mostrar: false, mensaje: '', tipo: 'success' }), 3000);
  };

  const registrarLog = async (accion: string, detalle?: string, entidadTipo?: string | null, entidadId?: string | null, fechaEvento?: string | Date) => {
    try {
      // Obtener información del usuario autenticado desde AuthContext
      const usuarioNombre = user?.nombre || user?.correo || 'Usuario no identificado';
      const usuarioEmail = user?.correo || 'sin-email';
      
      // Construir el detalle completo si no se proporciona
      const detalleCompleto = detalle || accion;
      
      // Normalizar la acción a mayúsculas para consistencia
      const accionNormalizada = accion.toUpperCase();
      
      // Determinar la fecha a usar: priorizar fechaEvento, luego fecha actual
      let fechaParaGuardar: string;
      if (fechaEvento) {
        // Si se proporciona una fecha del evento, convertirla a ISO string
        if (fechaEvento instanceof Date) {
          fechaParaGuardar = fechaEvento.toISOString();
        } else {
          // Si es string, verificar que sea válida
          const fechaParsed = new Date(fechaEvento);
          if (!isNaN(fechaParsed.getTime())) {
            fechaParaGuardar = fechaParsed.toISOString();
          } else {
            // Si no es válida, usar fecha actual
            fechaParaGuardar = new Date().toISOString();
          }
        }
      } else {
        // Si no se proporciona fecha, usar la fecha actual
        fechaParaGuardar = new Date().toISOString();
      }
      
      // ✅ COMENTADO: No insertar en tabla auditoria, solo usar auditoría desde gastos
      // Insertar en la tabla de auditoría de Supabase
      /*
      const { data, error } = await supabase
        .from('auditoria')
        .insert({
          usuario: usuarioNombre,
          accion: accionNormalizada,
          detalle: detalleCompleto,
          entidad_tipo: entidadTipo || null,
          entidad_id: entidadId || null,
          fecha: fechaParaGuardar
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Error al registrar log de auditoría:', error);
        // No mostrar error al usuario para no interrumpir el flujo, solo loguear en consola
      } else {
        console.log('✅ Log de auditoría registrado exitosamente:', {
          id: data.id,
          usuario: usuarioNombre,
          accion: accionNormalizada
        });
        
        // Actualizar la lista de auditoría si está visible
        if (mostrarAuditoria && data) {
          const nuevoLog = {
            id: data.id,
            usuario: usuarioNombre,
            accion: accionNormalizada,
            // Usar la fecha que se guardó en la base de datos, priorizando fecha, luego created_at
            fecha: data.fecha || data.created_at || new Date().toISOString(),
            detalle: detalleCompleto,
            resultado: 'OK'
          };
          setAuditoriaReal(prev => [nuevoLog, ...prev].slice(0, 50)); // Mantener solo los últimos 50
        }
      }
      */
      
      // ✅ CORREGIDO: Solo loguear en consola, no insertar en BD
      // La auditoría se genera automáticamente desde la tabla gastos
      console.log('✅ Log de auditoría (desde gastos):', {
        usuario: usuarioNombre,
        accion: accionNormalizada,
        detalle: detalleCompleto
      });
    } catch (err) {
      console.error('❌ Error inesperado al registrar log de auditoría:', err);
      // No mostrar error al usuario, solo loguear en consola
    }
  };

  const validarGasto = (gasto: { fecha?: string; trabajador?: string; descripcion?: string; monto?: string | number; categoria?: string; documento?: any; monto_ocr?: number }) => {
    // Si es trabajador y el campo trabajador está vacío, intentar usar el nombre del usuario
    const trabajadorParaValidar = userRole === 'Usuario' && !gasto.trabajador
      ? (user?.nombre || user?.correo?.split('@')[0] || '')
      : gasto.trabajador;
    
    if (!gasto.fecha || !trabajadorParaValidar || !gasto.descripcion || !gasto.monto) {
      mostrarToast('Todos los campos son obligatorios', 'error');
      return false;
    }
    const montoNumero = typeof gasto.monto === 'string' ? desformatearMonto(gasto.monto) : gasto.monto;
    if (montoNumero > 500000) {
      mostrarToast('⚠️ Gasto fuera de presupuesto', 'warning');
      return false;
    }
    // Verificar duplicados (solo mostrar mensaje a administradores)
    const duplicado = gastos.find(g => 
      g.descripcion === gasto.descripcion && 
      g.monto === montoNumero && 
      g.fecha === gasto.fecha
    );
    if (duplicado) {
      // Solo mostrar el mensaje de duplicado a administradores
      if (isAdministrador) {
        mostrarToast('Gasto duplicado detectado', 'error');
      }
      // Los trabajadores pueden continuar, pero no verán el mensaje
      return false;
    }
    return true;
  };

  const exportarMovimientos = async () => {
    if (!isAdministrador) {
      mostrarToast('No tienes permisos para exportar datos', 'error');
      return;
    }

    try {
      setCargando(true);
      mostrarToast('Exportando movimientos...', 'info');

      // Preparar datos para exportación
      const headers = ['Fecha', 'Trabajador', 'Descripción', 'Monto', 'Categoría', 'Estado'];
      const rows = gastosFiltrados.map(gasto => [
        formatDateDDMMYYYY(gasto.fecha),
        gasto.trabajador || 'Sin asignar',
        gasto.descripcion || '',
        `$${gasto.monto.toLocaleString('es-CL')}`,
        gasto.categoria || 'Sin categoría',
        gasto.sincronizado ? 'Guardado' : gasto.isPending ? 'Pendiente' : 'Error'
      ]);

      // Crear contenido CSV
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Crear y descargar archivo
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `movimientos_caja_chica_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setCargando(false);
      mostrarToast('Movimientos exportados exitosamente', 'success');
      await registrarLog('EXPORTAR', 'Movimientos de caja chica exportados', 'exportacion', null);
    } catch (error) {
      console.error('Error al exportar movimientos:', error);
      setCargando(false);
      mostrarToast('Error al exportar movimientos', 'error');
    }
  };

  const generarInformePDF = async () => {
    if (!isAdministrador) {
      mostrarToast('No tienes permisos para generar informes', 'error');
      return;
    }

    setCargando(true);
    mostrarToast('Generando informe PDF...', 'info');

    try {
      // Calcular estadísticas para el informe
      const totalMovimientos = gastosFiltrados.length;
      const totalGastado = gastosFiltrados.reduce((sum, g) => sum + g.monto, 0);
      const totalSincronizados = gastosFiltrados.filter(g => g.sincronizado).length;
      const totalPendientes = gastosFiltrados.filter(g => g.isPending).length;
      
      // Obtener período actual
      const periodoTexto = periodoFiltro === 'personalizado' 
        ? `${formatDateDDMMYYYY(fechaInicioPersonalizada)} - ${formatDateDDMMYYYY(fechaFinPersonalizada)}`
        : periodoFiltro.charAt(0).toUpperCase() + periodoFiltro.slice(1);
      
      // Ordenar gastos por fecha (más recientes primero)
      const gastosOrdenados = [...gastosFiltrados].sort((a, b) => {
        const fechaA = new Date(a.fecha).getTime();
        const fechaB = new Date(b.fecha).getTime();
        return fechaB - fechaA;
      });

      // Crear instancia de jsPDF
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 15;

      // Función auxiliar para agregar nueva página si es necesario
      const checkNewPage = (requiredSpace: number) => {
        if (yPosition + requiredSpace > pageHeight - margin) {
          doc.addPage();
          yPosition = margin;
          return true;
        }
        return false;
      };

      // Función para agregar texto con wrap
      const addText = (text: string, x: number, y: number, maxWidth: number, fontSize: number = 10, isBold: boolean = false) => {
        doc.setFontSize(fontSize);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        const lines = doc.splitTextToSize(text, maxWidth);
        doc.text(lines, x, y);
        return lines.length * (fontSize * 0.4);
      };

      // Encabezado
      doc.setFillColor(79, 70, 229); // #4F46E5
      doc.rect(0, 0, pageWidth, 40, 'F');
      doc.setTextColor(255, 255, 255);
      addText('INFORME DE CAJA CHICA', margin, 20, pageWidth - 2 * margin, 20, true);
      doc.setTextColor(0, 0, 0);
      yPosition = 50;

      // Información del período
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      addText(`Período: ${periodoTexto}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition += lineHeight;
      addText(`Fecha de generación: ${formatDateTimeFullDDMMYYYY(new Date())}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition += lineHeight;
      addText(`Generado por: ${user?.nombre || user?.correo || 'Sistema'}`, margin, yPosition, pageWidth - 2 * margin);
      yPosition += sectionSpacing;
      doc.setTextColor(0, 0, 0);

      // Resumen Ejecutivo
      checkNewPage(40);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(79, 70, 229);
      doc.text('RESUMEN EJECUTIVO', margin, yPosition);
      yPosition += lineHeight + 5;
      doc.setTextColor(0, 0, 0);

      // Tarjetas de estadísticas
      const cardWidth = (pageWidth - 2 * margin - 10) / 4;
      const cardHeight = 25;
      checkNewPage(cardHeight + 10);

      const stats = [
        { label: 'Total Movimientos', value: totalMovimientos.toString() },
        { label: 'Total Gastado', value: `$${totalGastado.toLocaleString('es-CL')}` },
        { label: 'Sincronizados', value: totalSincronizados.toString() },
        { label: 'Pendientes', value: totalPendientes.toString() }
      ];

      stats.forEach((stat, index) => {
        const x = margin + index * (cardWidth + 3.33);
        doc.setFillColor(249, 250, 251);
        doc.rect(x, yPosition, cardWidth, cardHeight, 'F');
        doc.setDrawColor(229, 231, 235);
        doc.rect(x, yPosition, cardWidth, cardHeight);
        
        doc.setFontSize(8);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(107, 114, 128);
        doc.text(stat.label, x + 5, yPosition + 8);
        
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(17, 24, 39);
        doc.text(stat.value, x + 5, yPosition + 18);
      });

      yPosition += cardHeight + sectionSpacing;

      // Desglose por Categoría
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(79, 70, 229);
      doc.text('DESGLOSE POR CATEGORÍA', margin, yPosition);
      yPosition += lineHeight + 5;
      doc.setTextColor(0, 0, 0);

      // Tabla de categorías
      checkNewPage(30);
      doc.setFillColor(79, 70, 229);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Categoría', margin + 2, yPosition + 6);
      doc.text('Monto', pageWidth - margin - 50, yPosition + 6);
      doc.text('Porcentaje', pageWidth - margin - 15, yPosition + 6);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);

      distribucionPorCategoria.forEach((cat, index) => {
        checkNewPage(7);
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(margin, yPosition - 2, pageWidth - 2 * margin, 7, 'F');
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(cat.categoria, margin + 2, yPosition + 5);
        doc.text(`$${(cat.monto as number).toLocaleString('es-CL')}`, pageWidth - margin - 50, yPosition + 5);
        doc.text(`${cat.porcentaje}%`, pageWidth - margin - 15, yPosition + 5);
        yPosition += 7;
      });

      yPosition += sectionSpacing;

      // Desglose por Trabajador
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(79, 70, 229);
      doc.text('DESGLOSE POR TRABAJADOR', margin, yPosition);
      yPosition += lineHeight + 5;
      doc.setTextColor(0, 0, 0);

      // Tabla de trabajadores
      checkNewPage(30);
      doc.setFillColor(79, 70, 229);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'bold');
      doc.text('Trabajador', margin + 2, yPosition + 6);
      doc.text('Total Gastado', pageWidth - margin - 50, yPosition + 6);
      doc.text('Movimientos', pageWidth - margin - 15, yPosition + 6);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);

      gastoPorTrabajadorLista.slice(0, 10).forEach((t, index) => {
        checkNewPage(7);
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(margin, yPosition - 2, pageWidth - 2 * margin, 7, 'F');
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const trabajadorText = t.trabajador.length > 25 ? t.trabajador.substring(0, 22) + '...' : t.trabajador;
        doc.text(trabajadorText, margin + 2, yPosition + 5);
        doc.text(`$${(t.gasto as number).toLocaleString('es-CL')}`, pageWidth - margin - 50, yPosition + 5);
        doc.text(gastosFiltrados.filter(g => g.trabajador === t.trabajador).length.toString(), pageWidth - margin - 15, yPosition + 5);
        yPosition += 7;
      });

      yPosition += sectionSpacing;

      // Movimientos Recientes
      checkNewPage(50);
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(79, 70, 229);
      doc.text('MOVIMIENTOS RECIENTES (ÚLTIMOS 20)', margin, yPosition);
      yPosition += lineHeight + 5;
      doc.setTextColor(0, 0, 0);

      // Tabla de movimientos
      checkNewPage(30);
      doc.setFillColor(79, 70, 229);
      doc.rect(margin, yPosition, pageWidth - 2 * margin, 8, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      doc.text('Fecha', margin + 2, yPosition + 6);
      doc.text('Trabajador', margin + 30, yPosition + 6);
      doc.text('Descripción', margin + 70, yPosition + 6);
      doc.text('Monto', pageWidth - margin - 35, yPosition + 6);
      doc.text('Estado', pageWidth - margin - 15, yPosition + 6);
      yPosition += 8;
      doc.setTextColor(0, 0, 0);

      gastosOrdenados.slice(0, 20).forEach((gasto, index) => {
        checkNewPage(7);
        if (index % 2 === 0) {
          doc.setFillColor(249, 250, 251);
          doc.rect(margin, yPosition - 2, pageWidth - 2 * margin, 7, 'F');
        }
        doc.setFontSize(7);
        doc.setFont('helvetica', 'normal');
        doc.text(formatDateDDMMYYYY(gasto.fecha), margin + 2, yPosition + 5);
        const trabajadorText = (gasto.trabajador || 'Sin asignar').length > 12 
          ? (gasto.trabajador || 'Sin asignar').substring(0, 9) + '...' 
          : (gasto.trabajador || 'Sin asignar');
        doc.text(trabajadorText, margin + 30, yPosition + 5);
        const descText = (gasto.descripcion || '').length > 25 
          ? (gasto.descripcion || '').substring(0, 22) + '...' 
          : (gasto.descripcion || '');
        doc.text(descText, margin + 70, yPosition + 5);
        doc.text(`$${gasto.monto.toLocaleString('es-CL')}`, pageWidth - margin - 35, yPosition + 5);
        const estado = gasto.sincronizado ? 'Guardado' : gasto.isPending ? 'Pendiente' : 'Error';
        doc.text(estado, pageWidth - margin - 15, yPosition + 5);
        yPosition += 7;
      });

      // Pie de página
      yPosition = pageHeight - 20;
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(107, 114, 128);
      doc.text('Informe generado el ' + formatDateTimeFullDDMMYYYY(new Date()) + ' desde el Sistema de Gestión Contable', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += lineHeight;
      doc.text('J&J Operations - Gestión Integral', pageWidth / 2, yPosition, { align: 'center' });

      // Generar nombre del archivo
      const nombreArchivo = `informe_caja_chica_${periodoTexto.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;

      // Descargar PDF
      doc.save(nombreArchivo);

      setCargando(false);
      mostrarToast('Informe PDF generado exitosamente', 'success');
      await registrarLog('CONSULTA', 'Informe PDF financiero generado desde Caja Chica', 'reporte', null);
    } catch (error) {
      console.error('Error al generar informe PDF:', error);
      setCargando(false);
      mostrarToast('Error al generar el informe PDF', 'error');
    }
  };

  const exportarAuditoria = async (formato: 'PDF' | 'Excel') => {
    try {
      if (formato === 'PDF') {
        // Crear contenido para PDF
        const contenido = `
AUDITORÍA AVANZADA DE CAJA CHICA
Fecha de exportación: ${formatDateTimeFullDDMMYYYY(new Date())}

RESUMEN:
- Total Movimientos: ${gastos.length}
- Gastos Aprobados: ${gastos.filter(g => g.aprobado).length}
- Gastos Pendientes: ${pendientes}

EVENTOS:
${(auditoriaReal.length > 0 
  ? auditoriaReal 
  : mockAuditoria.map(e => ({
      id: e.id.toString(),
      usuario: e.usuario,
      accion: e.accion,
      fecha: e.timestamp,
      detalle: e.detalles,
      resultado: e.resultado
    }))).map((evento, index) => `
${index + 1}. ${evento.accion}
   Usuario: ${evento.usuario}
   Fecha: ${formatDateTimeFullDDMMYYYY(evento.fecha || new Date().toISOString())}
   Detalles: ${evento.detalle || 'Sin detalle'}
   Resultado: ${evento.resultado || 'OK'}
`).join('\n')}
        `.trim();

        // Crear blob y descargar
        const blob = new Blob([contenido], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `auditoria_caja_chica_${new Date().toISOString().split('T')[0]}.txt`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        mostrarToast('Auditoría exportada como texto (PDF simulado)', 'success');
      } else if (formato === 'Excel') {
        // Crear CSV para Excel
        const headers = ['Fecha', 'Usuario', 'Acción', 'Detalles', 'Resultado'];
        // Usar auditoría real si está disponible, sino usar mock como fallback
        const eventosParaExportar = auditoriaReal.length > 0 
          ? auditoriaReal 
          : mockAuditoria.map(e => ({
              id: e.id.toString(),
              usuario: e.usuario,
              accion: e.accion,
              fecha: e.timestamp,
              detalle: e.detalles,
              resultado: e.resultado
            }));
        
        const rows = eventosParaExportar.map(evento => [
          formatDateTimeFullDDMMYYYY(evento.fecha || new Date().toISOString()),
          evento.usuario,
          evento.accion,
          evento.detalle || '',
          evento.resultado || 'OK'
        ]);
        
        const csvContent = [
          headers.join(','),
          ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');
        
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `auditoria_caja_chica_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        mostrarToast('Auditoría exportada como CSV (Excel)', 'success');
      }
      
    await registrarLog('EXPORTAR', `Auditoría exportada en formato ${formato}`, 'auditoria', null);
    } catch (error) {
      console.error('Error al exportar auditoría:', error);
      mostrarToast('Error al exportar la auditoría', 'error');
    }
  };

  const columnas = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'trabajador', label: 'Trabajador' },
    { key: 'descripcion', label: 'Descripción' },
    { key: 'monto', label: 'Monto' },
    { key: 'categoria', label: 'Categoría' },
    { key: 'estado', label: 'Estado' },
    { key: 'comprobante', label: 'Comprobante' }
  ];

  const filas = gastosFiltrados.map(gasto => ({
    fecha: formatDateDDMMYYYY(gasto.fecha),
    trabajador: gasto.trabajador,
    descripcion: gasto.descripcion,
    monto: `$${gasto.monto.toLocaleString('es-CL')}`,
    categoria: gasto.categoria,
    estado: (
      <div className="flex items-center space-x-2">
        {gasto.isPending ? (
          <span className="flex items-center text-orange-600 text-xs bg-orange-50 px-2 py-1 rounded-full">
            <Clock className="w-3 h-3 mr-1" />
            Pendiente por sincronizar
          </span>
        ) : gasto.sincronizado ? (
          <span className="flex items-center text-green-600 text-xs bg-green-50 px-2 py-1 rounded-full">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            Guardado
          </span>
        ) : (
          <span className="flex items-center text-red-600 text-xs bg-red-50 px-2 py-1 rounded-full">
            <AlertCircle className="w-3 h-3 mr-1" />
            Error al guardar
          </span>
        )}
      </div>
    ),
    comprobante: (
      <button
        onClick={() => {
          setGastoSeleccionadoModal(gasto);
          setGastoModalAbierto(true);
        }}
        className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
        title="Ver comprobante"
      >
        <FileText className="w-3.5 h-3.5 mr-1.5" />
        Ver Boleta
      </button>
    )
  }));

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header Profesional */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div className="flex-1">
              <nav className="flex items-center space-x-2 text-xs sm:text-sm text-gray-500 mb-2 sm:mb-3">
                <ChevronRight className="w-3 h-3" />
                <span className="text-gray-900 font-medium">Caja Chica</span>
              </nav>
              <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 tracking-tight">Gestión de Caja Chica</h1>
              <p className="text-xs sm:text-sm text-gray-500 mt-1">Control y seguimiento de gastos operativos</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex items-center space-x-2 px-2 sm:px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                <Shield className="w-3 h-3 sm:w-4 sm:h-4 text-gray-600" />
                <span className="text-xs sm:text-sm text-gray-700 font-medium">{rolParaMostrar}</span>
              </div>
              {puedeCrearGastos && (
                <button
                  onClick={() => {
                    // Si es trabajador, establecer automáticamente el trabajador a su nombre
                    const trabajadorInicial = userRole === 'Usuario' 
                      ? (user?.nombre || user?.correo?.split('@')[0] || '')
                      : '';
                    
                    setNuevoGasto({
                      fecha: '',
                      trabajador: trabajadorInicial,
                      descripcion: '',
                      monto: '',
                      categoria: '',
                      documento: null,
                      monto_ocr: undefined
                    });
                    setMostrarModal(true);
                  }}
                  className="px-3 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 shadow-sm transition-all font-medium text-xs sm:text-sm"
                >
                  <Plus className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">Nuevo Gasto</span>
                  <span className="sm:hidden">Nuevo</span>
                </button>
              )}
              {hasPermission('view_auditoria') && (
                <button 
                  onClick={() => setMostrarAuditoria(!mostrarAuditoria)}
                  className="px-3 sm:px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-2 border border-gray-300 transition-all font-medium text-xs sm:text-sm"
                >
                  <History className="w-3 h-3 sm:w-4 sm:h-4" />
                  <span className="hidden sm:inline">{mostrarAuditoria ? 'Ocultar' : 'Ver'} Auditoría</span>
                  <span className="sm:hidden">Audit.</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Filtro de Período */}
        <Card className="mb-4 sm:mb-6 shadow-xl border-2 border-indigo-300 bg-gradient-to-r from-indigo-50 via-blue-50 to-white">
          <div className="p-4 sm:p-5">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
              <div className="flex items-center gap-3 sm:gap-4 w-full lg:w-auto">
                <div className="p-2 sm:p-3 bg-indigo-100 rounded-xl shadow-sm flex-shrink-0">
                  <Calendar className="w-5 h-5 sm:w-7 sm:h-7 text-indigo-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1">Filtro de Período</h3>
                  <p className="text-xs sm:text-sm text-gray-600 flex items-center gap-2">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                      <RefreshCw className="w-3 h-3" />
                      <span className="hidden sm:inline">Actualiza todos los componentes</span>
                      <span className="sm:hidden">Actualiza</span>
                    </span>
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-3 sm:gap-4 w-full lg:w-auto">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 w-full">
                  <label htmlFor="periodo-selector-caja-chica" className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    Período:
                  </label>
                  <select
                    id="periodo-selector-caja-chica"
                    value={periodoFiltro}
                    onChange={(e) => {
                      const nuevoPeriodo = e.target.value as 'enero' | 'febrero' | 'marzo' | 'abril' | 'mayo' | 'junio' | 'julio' | 'agosto' | 'septiembre' | 'octubre' | 'noviembre' | 'diciembre' | 'trimestre' | 'año' | 'personalizado';
                      setPeriodoFiltro(nuevoPeriodo);
                      if (nuevoPeriodo !== 'personalizado') {
                        setFechaInicioPersonalizada('');
                        setFechaFinPersonalizada('');
                      }
                    }}
                    className="px-3 sm:px-5 py-2 sm:py-3 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-xs sm:text-sm font-bold focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-md hover:shadow-lg w-full sm:min-w-[250px]"
                  >
                    <optgroup label="Meses del Año">
                      <option value="enero">Enero</option>
                      <option value="febrero">Febrero</option>
                      <option value="marzo">Marzo</option>
                      <option value="abril">Abril</option>
                      <option value="mayo">Mayo</option>
                      <option value="junio">Junio</option>
                      <option value="julio">Julio</option>
                      <option value="agosto">Agosto</option>
                      <option value="septiembre">Septiembre</option>
                      <option value="octubre">Octubre</option>
                      <option value="noviembre">Noviembre</option>
                      <option value="diciembre">Diciembre</option>
                    </optgroup>
                    <optgroup label="Períodos">
                      <option value="trimestre">Trimestral (Último trimestre completo)</option>
                      <option value="año">Anual (Año actual completo)</option>
                      <option value="personalizado">Rango personalizado (Calendario)</option>
                    </optgroup>
                  </select>
                </div>
                
                {/* Calendario para rango personalizado */}
                {periodoFiltro === 'personalizado' && (
                  <div className="flex flex-col gap-3 p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-200 w-full">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Calendar className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <label htmlFor="fecha-inicio-caja-chica" className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                          Desde:
                        </label>
                        <input
                          id="fecha-inicio-caja-chica"
                          type="date"
                          value={fechaInicioPersonalizada}
                          onChange={(e) => setFechaInicioPersonalizada(e.target.value)}
                          max={fechaFinPersonalizada || undefined}
                          className="flex-1 sm:flex-none px-2 sm:px-3 py-2 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
                        />
                      </div>
                      <div className="flex items-center gap-2 w-full sm:w-auto">
                        <Calendar className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                        <label htmlFor="fecha-fin-caja-chica" className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                          Hasta:
                        </label>
                        <input
                          id="fecha-fin-caja-chica"
                          type="date"
                          value={fechaFinPersonalizada}
                          onChange={(e) => setFechaFinPersonalizada(e.target.value)}
                          min={fechaInicioPersonalizada || undefined}
                          max={new Date().toISOString().split('T')[0]}
                          className="flex-1 sm:flex-none px-2 sm:px-3 py-2 border-2 border-indigo-300 rounded-lg bg-white text-gray-900 text-xs sm:text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all shadow-sm"
                        />
                      </div>
                    </div>
                    {fechaInicioPersonalizada && fechaFinPersonalizada && (
                      <div className="text-xs text-indigo-700 font-medium text-center sm:text-left">
                        {new Date(fechaInicioPersonalizada).toLocaleDateString('es-CL')} - {new Date(fechaFinPersonalizada).toLocaleDateString('es-CL')}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Panel de KPIs Principales - Diseño Mejorado */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card className="bg-gradient-to-br from-white to-blue-50/30 border border-blue-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative">
            {/* Decoración de fondo */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-blue-200/10 rounded-full -mr-16 -mt-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-300/5 rounded-full -ml-12 -mb-12"></div>
            
            <div className="p-4 sm:p-6 relative z-10">
              <div className="flex items-start justify-start mb-4 gap-2">
                <div className="flex items-center space-x-2 sm:space-x-4 flex-1 min-w-0">
                  <div className="p-2 sm:p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg shadow-blue-500/30 flex-shrink-0">
                    <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Gasto del Mes</p>
                    <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 mt-1 truncate" data-testid="widget-gasto-mes-actual">
                      ${gastoMostrar.toLocaleString('es-CL')}
                    </p>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-blue-100/50">
                <p className="text-xs text-gray-500 font-medium">
                  {gastoMesAnterior > 0 
                    ? `Comparado con el período anterior ($${gastoMesAnterior.toLocaleString('es-CL')})`
                    : 'Sin datos del período anterior para comparar'
                  }
                </p>
                
                {/* Sección expandible solo para Administradores */}
                {isAdministrador && (
                  <>
                    <button
                      onClick={() => setMostrarAnalisisGastoMes(!mostrarAnalisisGastoMes)}
                      className="mt-4 w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 text-sm font-semibold text-blue-700 transition-all duration-200 group"
                    >
                      <span className="flex items-center gap-2">
                        <BarChart3 className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        {mostrarAnalisisGastoMes ? 'Ocultar' : 'Ver'} Análisis Detallado
                      </span>
                      {mostrarAnalisisGastoMes ? (
                        <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      ) : (
                        <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      )}
                    </button>
                    
                    {mostrarAnalisisGastoMes && (
                      <div className="mt-4 pt-4 border-t border-blue-100/50 space-y-4 animate-in fade-in slide-in-from-top-2 duration-300">
                        {/* Métricas Rápidas */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all hover:scale-105">
                            <p className="text-xs font-medium text-gray-500 mb-1.5">Promedio Diario</p>
                            <p className="text-lg font-bold text-gray-900">
                              ${gastoPromedioDiario.toLocaleString('es-CL')}<span className="text-sm font-normal text-gray-500">/día</span>
                            </p>
                          </div>
                          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all hover:scale-105">
                            <p className="text-xs font-medium text-gray-500 mb-1.5">Progreso del Período</p>
                            <p className="text-lg font-bold text-gray-900">
                              Día {diaActualDelPeriodo > 0 ? diaActualDelPeriodo : diasDelPeriodo} <span className="text-sm font-normal text-gray-500">de {diasDelPeriodo}</span>
                            </p>
                          </div>
                          <div className="bg-white/80 backdrop-blur-sm p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all hover:scale-105">
                            <p className="text-xs font-medium text-gray-500 mb-1.5">Proyección Mensual</p>
                            <p className="text-lg font-bold text-gray-900">
                              ${gastoProyectadoMes.toLocaleString('es-CL')}
                            </p>
                          </div>
                        </div>

                        {/* Comparación */}
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-200 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <BarChart3 className="w-4 h-4 text-blue-600" />
                            <p className="text-sm font-bold text-blue-900">Comparación</p>
                          </div>
                          <div className="space-y-2 text-sm">
                            {gastoMesAnterior > 0 && (
                              <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                                <span className="text-gray-600 font-medium">Mes anterior:</span>
                                <span className="font-bold text-gray-900">${gastoMesAnterior.toLocaleString('es-CL')}</span>
                              </div>
                            )}
                            <div className="flex justify-between items-center p-2 bg-white/60 rounded-lg">
                              <span className="text-gray-600 font-medium">Promedio 3 meses:</span>
                              <span className="font-bold text-gray-900">${promedioUltimos3Meses.toLocaleString('es-CL')}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-white to-emerald-50/30 border border-emerald-100 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden relative">
            {/* Decoración de fondo */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-emerald-200/10 rounded-full -ml-16 -mt-16"></div>
            <div className="absolute bottom-0 right-0 w-24 h-24 bg-emerald-300/5 rounded-full -mr-12 -mb-12"></div>
            
            <div className="p-4 sm:p-6 relative z-10">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-3 sm:gap-0">
                <div className="flex items-center space-x-3 sm:space-x-4 w-full sm:w-auto">
                  <div className="p-2.5 sm:p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl shadow-lg shadow-emerald-500/30 flex-shrink-0">
                    <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Saldo Disponible</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1 break-words" data-testid="widget-saldo-disponible">
                      ${saldoDisponible.toLocaleString('es-CL')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-1.5 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-green-50 border border-emerald-200 rounded-lg shadow-sm flex-shrink-0 self-start sm:self-auto">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-bold text-emerald-700">Disponible</span>
                </div>
              </div>
              <div className="pt-4 border-t border-emerald-100/50">
                
                {/* Barra de progreso del presupuesto mejorada */}
                <div className="space-y-3">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs gap-1 sm:gap-0">
                    <span className="text-gray-600 font-semibold">Utilización del Presupuesto</span>
                    <span className={`font-bold text-sm ${
                      porcentajePresupuesto >= 90 ? 'text-red-600' : 
                      porcentajePresupuesto >= 70 ? 'text-orange-600' : 
                      'text-emerald-600'
                    }`}>
                      {porcentajePresupuesto}%
                          </span>
                        </div>
                  <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                    <div 
                      className={`h-3 rounded-full transition-all duration-500 ${
                        porcentajePresupuesto >= 90 ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                        porcentajePresupuesto >= 70 ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 
                        'bg-gradient-to-r from-emerald-500 to-green-600'
                      }`}
                      style={{ width: `${Math.min(porcentajePresupuesto, 100)}%` }}
                    />
                    </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs gap-2 sm:gap-0">
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-gray-400 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-600 font-medium break-words">Gastado: <span className="font-bold text-gray-900">${gastoMostrar.toLocaleString('es-CL')}</span></span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></div>
                      <span className="text-gray-600 font-medium break-words">Disponible: <span className="font-bold text-emerald-700">${saldoDisponible.toLocaleString('es-CL')}</span></span>
                    </div>
                  </div>
                </div>

                {/* Sección expandible Detalle por Trabajador - Solo para Administradores */}
                {isAdministrador && (
                  <>
                    <button
                      onClick={() => setMostrarDetalleTrabajadores(!mostrarDetalleTrabajadores)}
                      className="mt-4 w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-emerald-50 to-green-50 hover:from-emerald-100 hover:to-green-100 rounded-lg border border-emerald-200 text-sm font-semibold text-emerald-700 transition-all duration-200 group"
                    >
                      <span className="flex items-center gap-2">
                        <User className="w-4 h-4 group-hover:scale-110 transition-transform" />
                        {mostrarDetalleTrabajadores ? 'Ocultar' : 'Ver'} Detalle por Trabajador
                      </span>
                      {mostrarDetalleTrabajadores ? (
                        <ChevronUp className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      ) : (
                        <ChevronDown className="w-4 h-4 group-hover:scale-110 transition-transform" />
                      )}
                    </button>
                    
                    {mostrarDetalleTrabajadores && (
                      <div className="mt-4 pt-4 border-t border-emerald-100/50 space-y-3 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="bg-gradient-to-br from-emerald-50 to-green-50 p-4 rounded-xl border border-emerald-200 shadow-sm">
                          <div className="flex items-center gap-2 mb-3">
                            <User className="w-4 h-4 text-emerald-600" />
                            <p className="text-sm font-bold text-emerald-900">Estado por Trabajador</p>
                          </div>
                          <div className="space-y-2.5">
                            {saldoPorTrabajador.map((trabajador) => {
                              const getEstadoBadge = () => {
                                if (trabajador.estado === 'excedido') {
                                  return (
                                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                      <AlertCircle className="w-3 h-3" />
                                      Excedido
                                    </span>
                                  );
                                } else if (trabajador.estado === 'critico') {
                                  return (
                                    <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                      <AlertTriangle className="w-3 h-3" />
                                      Crítico
                                    </span>
                                  );
                                } else {
                                  return (
                                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-semibold flex items-center gap-1">
                                      <CheckCircle className="w-3 h-3" />
                                      Normal
                                    </span>
                                  );
                                }
                              };

                              return (
                                <div key={trabajador.id} className="bg-white/70 p-3 rounded-lg border border-emerald-100 hover:shadow-md transition-all">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="font-semibold text-gray-900">{trabajador.nombre}</span>
                                    {getEstadoBadge()}
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                    <div>
                                      <span className="text-gray-600">Gastado:</span>
                                      <span className="font-bold text-gray-900 ml-1">
                                        ${trabajador.gasto.toLocaleString('es-CL')}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="text-gray-600">Disponible:</span>
                                      <span className={`font-bold ml-1 ${
                                        trabajador.saldo < 0 ? 'text-red-600' : 'text-emerald-700'
                                      }`}>
                                        ${trabajador.saldo.toLocaleString('es-CL')}
                                      </span>
                                    </div>
                                    <div className="col-span-2">
                                      <div className="flex items-center justify-between mb-1">
                                        <span className="text-gray-600">Presupuesto: ${trabajador.presupuesto.toLocaleString('es-CL')}</span>
                                        <span className={`font-bold ${
                                          trabajador.porcentajeUtilizado >= 90 ? 'text-red-600' : 
                                          trabajador.porcentajeUtilizado >= 70 ? 'text-orange-600' : 
                                          'text-emerald-600'
                                        }`}>
                                          {trabajador.porcentajeUtilizado}%
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                                        <div 
                                          className={`h-2 rounded-full transition-all duration-500 ${
                                            trabajador.porcentajeUtilizado >= 90 ? 'bg-gradient-to-r from-red-500 to-red-600' : 
                                            trabajador.porcentajeUtilizado >= 70 ? 'bg-gradient-to-r from-orange-500 to-orange-600' : 
                                            'bg-gradient-to-r from-emerald-500 to-green-600'
                                          }`}
                                          style={{ width: `${Math.min(trabajador.porcentajeUtilizado, 100)}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* Tabla de Saldo por Trabajador - Solo para Administradores */}
        {hasPermission('view_all_expenses') && saldoPorTrabajador.length > 0 && (
          <Card className="mb-8 bg-white border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-600" />
                  Saldo Disponible por Trabajador
                </h3>
                <span className="text-xs text-gray-500">
                  Presupuesto mensual: ${PRESUPUESTO_MENSUAL_POR_TRABAJADOR.toLocaleString('es-CL')} por trabajador
                </span>
              </div>
            </div>
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">Trabajador</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">Presupuesto</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">Gasto del Mes</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">Saldo Disponible</th>
                      <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">% Utilizado</th>
                      <th className="text-center py-3 px-4 text-xs font-semibold text-gray-700 uppercase tracking-wide">Estado</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {saldoPorTrabajador.map((trabajador) => (
                      <tr key={trabajador.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{trabajador.nombre}</p>
                            <p className="text-xs text-gray-500">{trabajador.email}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm text-gray-700">
                            ${trabajador.presupuesto.toLocaleString('es-CL')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className="text-sm font-medium text-gray-900">
                            ${trabajador.gasto.toLocaleString('es-CL')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={`text-sm font-semibold ${
                            trabajador.estado === 'excedido' ? 'text-red-600' : 
                            trabajador.estado === 'critico' ? 'text-orange-600' : 
                            'text-green-600'
                          }`}>
                            ${trabajador.saldo.toLocaleString('es-CL')}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex items-center justify-end">
                            <span className={`text-sm font-medium ${
                              trabajador.porcentajeUtilizado >= 100 ? 'text-red-600' : 
                              trabajador.porcentajeUtilizado >= 90 ? 'text-orange-600' : 
                              'text-gray-700'
                            }`}>
                              {trabajador.porcentajeUtilizado}%
                            </span>
                            <div className="ml-2 w-16 bg-gray-200 rounded-full h-2">
                              <div 
                                className={`h-2 rounded-full ${
                                  trabajador.porcentajeUtilizado >= 100 ? 'bg-red-500' : 
                                  trabajador.porcentajeUtilizado >= 90 ? 'bg-orange-500' : 
                                  'bg-green-500'
                                }`}
                                style={{ width: `${Math.min(trabajador.porcentajeUtilizado, 100)}%` }}
                              />
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            trabajador.estado === 'excedido' 
                              ? 'bg-red-100 text-red-800' 
                              : trabajador.estado === 'critico' 
                              ? 'bg-orange-100 text-orange-800' 
                              : 'bg-green-100 text-green-800'
                          }`}>
                            {trabajador.estado === 'excedido' ? 'Excedido' : 
                             trabajador.estado === 'critico' ? 'Crítico' : 
                             'Normal'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="py-3 px-4 text-sm font-semibold text-gray-900">Total</td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                        ${(saldoPorTrabajador.length * PRESUPUESTO_MENSUAL_POR_TRABAJADOR).toLocaleString('es-CL')}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                        ${gastoMesActual.toLocaleString('es-CL')}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                        ${saldoDisponible.toLocaleString('es-CL')}
                      </td>
                      <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900">
                        {PRESUPUESTO_MENSUAL > 0 ? Math.round((gastoMesActual / PRESUPUESTO_MENSUAL) * 100) : 0}%
                      </td>
                      <td className="py-3 px-4"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </Card>
        )}

        {/* Dashboard de Analítica */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Gráfico de Distribución por Categoría */}
          <Card className="bg-white border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <PieChart className="w-4 h-4 mr-2 text-gray-600" />
                  Distribución por Categoría
                </h3>
              </div>
            </div>
          <div className="p-6">
            {distribucionPorCategoria.length > 0 ? (
              <div className="h-64">
                <PieChartComponent
                  data={distribucionPorCategoria.map(item => ({
                    name: item.categoria,
                    value: item.monto
                  }))}
                  title=""
                  description=""
                />
              </div>
            ) : (
              <div className="h-64 flex items-center justify-center bg-gray-50 rounded-lg">
                <div className="text-center">
                  <PieChart className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No hay datos disponibles</p>
                </div>
              </div>
            )}
          </div>
        </Card>

          {/* Alertas e Irregularidades */}
          {(userRole === 'Administrador' || userRole === 'Contadora') && (
          <div style={{ position: 'relative', zIndex: 1 }}>
            <Card className="bg-white border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center">
                    <Bell className="w-4 h-4 mr-2 text-gray-600" />
                    Gestión de Gastos
                  </h3>
                </div>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {(() => {
                    // Filtrar anomalías: mostrar solo las que no están aprobadas
                    const anomaliasPendientes = anomalias
                      .filter(a => a.estado !== 'aprobado')
                      .sort((a, b) => {
                        // Ordenar por fecha: más recientes primero
                        const fechaA = new Date(a.fecha).getTime();
                        const fechaB = new Date(b.fecha).getTime();
                        return fechaB - fechaA; // Orden descendente (más reciente primero)
                      });
                    if (anomaliasPendientes.length === 0) {
                      return (
                        <div className="text-center text-gray-500 text-sm py-8">
                          <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-2" />
                          <p>No se detectaron anomalías</p>
                          <p className="text-xs text-gray-400 mt-1">Todos los gastos están dentro de los parámetros normales</p>
                        </div>
                      );
                    }
                    return anomaliasPendientes.map((anomalia) => (
                      <div 
                        key={anomalia.id} 
                        className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        style={{ position: 'relative', zIndex: 10000 }}
                      >
                      <div className="flex items-center space-x-4 flex-1 min-w-0 w-full sm:w-auto">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          anomalia.severidad === 'rojo' ? 'bg-red-500' :
                          anomalia.severidad === 'amarillo' ? 'bg-yellow-500' : 'bg-green-500'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{anomalia.descripcion}</p>
                          <p className="text-sm text-gray-500">{formatDateDDMMYYYY(anomalia.fecha)}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end" style={{ position: 'relative', zIndex: 10001 }}>
                        <div className="text-right sm:text-left">
                          <p className="font-semibold text-gray-900">${anomalia.monto.toLocaleString()}</p>
                          <span className={`text-xs px-2 py-1 rounded-full inline-block mt-1 ${
                            anomalia.estado === 'pendiente' ? 'bg-yellow-100 text-yellow-700' :
                            anomalia.estado === 'aprobado' ? 'bg-green-100 text-green-700' : 
                            'bg-red-100 text-red-700'
                          }`}>
                            {anomalia.estado}
                          </span>
                        </div>
                        
                        {/* Selector de estado - Dropdown personalizado */}
                        <div style={{ position: 'relative', zIndex: 10002 }}>
                          <DropdownEstado
                            anomalia={anomalia}
                            dropdownAbierto={dropdownAbierto}
                            setDropdownAbierto={setDropdownAbierto}
                            dropdownAbiertoRef={dropdownAbiertoRef}
                            actualizandoEstado={actualizandoEstado}
                            handleActualizarEstadoAlerta={handleActualizarEstadoAlerta}
                          />
                        </div>
                        
                        {/* Botón Ver */}
                        <BotonVer 
                          anomalia={anomalia}
                          handleVerDetalleAlerta={handleVerDetalleAlerta}
                        />
                      </div>
                    </div>
                  ));
                  })()}
              </div>
            </div>
          </Card>
          </div>
          )}

        {/* ✅ NUEVO: Módulo Pendientes - Solo para Trabajadores */}
        {userRole === 'Usuario' && gastosPendientesTrabajador.length > 0 && (
          <Card className="bg-white border border-gray-200 shadow-sm">
            <div className="px-6 py-4 border-b border-gray-200 bg-yellow-50">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold text-gray-900 flex items-center">
                  <Clock className="w-4 h-4 mr-2 text-yellow-600" />
                  Pendientes
                </h3>
                <span className="text-xs text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full font-medium">
                  {gastosPendientesTrabajador.length} {gastosPendientesTrabajador.length === 1 ? 'gasto' : 'gastos'}
                </span>
              </div>
              <p className="text-xs text-yellow-700 mt-2">
                Estos gastos están pendientes de revisión y no se incluyen en tu total
              </p>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                {gastosPendientesTrabajador.map((gasto: any) => {
                  // ✅ CORREGIDO: Buscar anomalía asociada solo si es PENDIENTE (no rechazada)
                  // Los gastos rechazados no deben aparecer en el panel de pendientes
                  const anomaliaAsociada = anomalias
                    .filter(a => a.estado === 'pendiente') // Solo pendientes, no rechazadas
                    .find(a => String(a.gasto.id) === String(gasto.id));
                  
                  return (
                    <div
                      key={gasto.id}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-yellow-200"
                    >
                      <div className="flex items-center space-x-4 flex-1 min-w-0 w-full sm:w-auto">
                        <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                          anomaliaAsociada?.severidad === 'rojo' ? 'bg-red-500' :
                          anomaliaAsociada?.severidad === 'amarillo' ? 'bg-yellow-500' : 
                          'bg-yellow-500'
                        }`}></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">
                            {gasto.descripcion}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-sm text-gray-500">
                              {formatDateDDMMYYYY(gasto.fecha)}
                            </p>
                            <span className="text-xs text-gray-400">•</span>
                            <p className="text-sm text-gray-500">{gasto.categoria}</p>
                          </div>
                          {anomaliaAsociada && (
                            <p className="text-xs text-yellow-700 mt-1 italic">
                              {anomaliaAsociada.descripcion}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-3 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                        <div className="text-right sm:text-left">
                          <p className="font-semibold text-gray-900">
                            ${gasto.monto.toLocaleString('es-CL')}
                          </p>
                          <span className="text-xs px-2 py-1 rounded-full inline-block mt-1 bg-yellow-100 text-yellow-700">
                            Pendiente
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            setGastoSeleccionadoModal(gasto);
                            setGastoModalAbierto(true);
                          }}
                          className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 hover:text-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 transition-colors"
                          title="Ver detalle"
                        >
                          <FileText className="w-3.5 h-3.5 mr-1.5" />
                          Ver Detalle
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}
        </div>

        {/* Tabla de Movimientos */}
        <Card className="bg-white border border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <h3 className="text-sm sm:text-base font-semibold text-gray-900">Movimientos de Caja Chica</h3>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              {isAdministrador && (
                <>
                  <button 
                    onClick={exportarMovimientos}
                    disabled={cargando}
                    className="px-2.5 sm:px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1.5 sm:space-x-2 border border-gray-300 text-xs sm:text-sm font-medium disabled:opacity-50 flex-1 sm:flex-none justify-center"
                  >
                    {cargando ? (
                      <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    )}
                    <span className="hidden sm:inline">{cargando ? 'Exportando...' : 'Exportar'}</span>
                    <span className="sm:hidden">{cargando ? 'Exp...' : 'Exportar'}</span>
                  </button>
                  <button 
                    onClick={generarInformePDF}
                    disabled={cargando}
                    className="px-2.5 sm:px-3 py-1.5 bg-white text-gray-700 rounded-lg hover:bg-gray-50 flex items-center space-x-1.5 sm:space-x-2 border border-gray-300 text-xs sm:text-sm font-medium disabled:opacity-50 flex-1 sm:flex-none justify-center"
                  >
                    {cargando ? (
                      <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
                    ) : (
                      <FileSpreadsheet className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    )}
                    <span className="hidden sm:inline">{cargando ? 'Generando...' : 'Informe'}</span>
                    <span className="sm:hidden">{cargando ? 'Gen...' : 'Informe'}</span>
                  </button>
                </>
              )}
            </div>
          </div>
        <Table columns={columnas} data={filas} />
        
          {/* Resumen de la Tabla */}
          <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between text-xs text-gray-600 gap-2 sm:gap-0">
              <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4">
                <span>Total de movimientos: <strong className="text-gray-900">{gastosFiltrados.length}</strong></span>
                <span>Total sincronizados: <strong className="text-gray-900">{gastosFiltrados.filter(g => g.sincronizado).length}</strong></span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-3 h-3 flex-shrink-0" />
                <span className="break-words sm:break-normal">Última actualización: {formatDateTimeFullDDMMYYYY(new Date())}</span>
              </div>
            </div>
          </div>
        </Card>

        {/* Modal Agregar Gasto */}
        {mostrarModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-md shadow-xl max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Nuevo Gasto</h3>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            <div className="overflow-y-auto flex-1 p-6 space-y-4">
              {/* Banner de duplicado - Solo visible para administradores */}
              {isAdministrador && gastoDuplicadoDetectado && (
                <div className="bg-red-500 text-white p-3 mb-4 rounded-lg flex items-center justify-between">
                  <div className="flex items-center">
                    <XCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                    <span className="text-sm font-medium">Gasto duplicado detectado</span>
                  </div>
                  <button
                    onClick={() => setGastoDuplicadoDetectado(false)}
                    className="text-white hover:text-red-100 transition-colors ml-2"
                    aria-label="Cerrar"
                  >
                    <XCircle className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha *</label>
                <div className="relative">
                  <input
                    type="date"
                    value={nuevoGasto.fecha || ''}
                    onChange={(e) => {
                      const fechaISO = e.target.value;
                      if (fechaISO) {
                        // ✅ Validar que no sea una fecha pasada (solo permitir desde hoy en adelante)
                        // Comparar fechas como strings YYYY-MM-DD para evitar problemas de zona horaria
                        const hoyISO = new Date().toISOString().split('T')[0]; // Formato: YYYY-MM-DD
                        
                        if (fechaISO < hoyISO) {
                          mostrarToast('La fecha no puede ser pasada. Solo se permiten fechas desde hoy en adelante', 'error');
                          setNuevoGasto({...nuevoGasto, fecha: ''});
                        } else {
                          setNuevoGasto({...nuevoGasto, fecha: fechaISO});
                        }
                      } else {
                        setNuevoGasto({...nuevoGasto, fecha: ''});
                      }
                    }}
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  {/* Mostrar formato DD-MM-AAAA como texto debajo del input */}
                  {nuevoGasto.fecha && (
                    <p className="mt-1 text-xs text-gray-600">
                      {convertirISOaDDMMYYYY(nuevoGasto.fecha)}
                    </p>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500">Formato: DD-MM-AAAA (ej: 13-12-2025)</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trabajador *</label>
                {userRole === 'Usuario' ? (
                  // Si es trabajador, mostrar el campo como solo lectura
                  <input
                    type="text"
                    value={user?.nombre || user?.correo?.split('@')[0] || nuevoGasto.trabajador}
                    onChange={(e) => {
                      // Aunque esté deshabilitado, mantener sincronizado el estado
                      setNuevoGasto({...nuevoGasto, trabajador: user?.nombre || user?.correo?.split('@')[0] || ''});
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed"
                    readOnly
                    disabled
                  />
                ) : (
                  // Si es administrador o contadora, permitir seleccionar trabajador
                  <select
                    value={nuevoGasto.trabajador}
                    onChange={(e) => setNuevoGasto({...nuevoGasto, trabajador: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                    disabled={cargandoTrabajadores}
                  >
                    <option value="">{cargandoTrabajadores ? 'Cargando trabajadores...' : 'Seleccionar trabajador'}</option>
                    {trabajadores.map((trabajador) => (
                      <option key={trabajador.id} value={trabajador.nombre}>
                        {trabajador.nombre} ({trabajador.email})
                      </option>
                    ))}
                  </select>
                )}
                {trabajadores.length === 0 && !cargandoTrabajadores && userRole !== 'Usuario' && (
                  <p className="text-xs text-gray-500 mt-1">No hay trabajadores disponibles en la base de datos</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Descripción *</label>
                <input
                  type="text"
                  value={nuevoGasto.descripcion}
                  onChange={(e) => setNuevoGasto({...nuevoGasto, descripcion: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monto *</label>
                <input
                  type="text"
                  value={nuevoGasto.monto}
                  onChange={(e) => {
                    const valorFormateado = formatearMonto(e.target.value);
                    setNuevoGasto({...nuevoGasto, monto: valorFormateado});
                  }}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Categoría *</label>
                <select
                  value={nuevoGasto.categoria}
                  onChange={(e) => setNuevoGasto({...nuevoGasto, categoria: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Seleccionar categoría</option>
                  {categorias.slice(1).map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>
              
              {/* Componente de carga de imagen con OCR */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagen de la Boleta
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Sube la boleta para lectura automática
                </p>
                <ImageUploadWithOCR
                  onImageUpload={handleImageUpload}
                  onOCRComplete={handleOCRComplete}
                  onImageRemove={handleImageRemove}
                  currentImage={imagenBoleta}
                />
              </div>
            </div>
              <div className="flex justify-end space-x-3 p-6 pt-4 border-t border-gray-200 flex-shrink-0">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm font-medium transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleAgregarGasto}
                  disabled={cargando || !nuevoGasto.fecha || !nuevoGasto.descripcion || !nuevoGasto.monto || !nuevoGasto.categoria || (userRole !== 'Usuario' && !nuevoGasto.trabajador)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cargando ? 'Guardando...' : 'Agregar Gasto'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Drawer de Detalle */}
        {mostrarDrawer && gastoSeleccionado && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50">
            <div className="absolute right-0 top-0 h-full w-96 bg-white shadow-xl">
              <div className="p-6 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">Detalle del Gasto</h3>
                  <button
                    onClick={() => setMostrarDrawer(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <XCircle className="w-5 h-5" />
                  </button>
                </div>
              </div>
            <div className="p-6 space-y-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Información del Gasto</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-medium">{gastoSeleccionado.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Fecha:</span>
                    <span className="font-medium">{formatDateDDMMYYYY(gastoSeleccionado.fecha)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Trabajador:</span>
                    <span className="font-medium">{gastoSeleccionado.trabajador}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monto:</span>
                    <span className="font-medium">${gastoSeleccionado.monto.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Historial de Movimientos</h4>
                <div className="space-y-3">
                  {(() => {
                    const gastoWithHistorial = gastoSeleccionado as Gasto & { historial?: Array<{ accion: string; usuario: string; fecha: string }> };
                    if (gastoWithHistorial.historial && Array.isArray(gastoWithHistorial.historial)) {
                      return gastoWithHistorial.historial.map((evento, index: number) => {
                        // Formatear fecha con hora exacta
                        const fechaFormateada = formatDateTimeDDMMYYYY(evento.fecha);
                        const fechaObj = new Date(evento.fecha);
                        const horaExacta = `${fechaObj.getHours().toString().padStart(2, '0')}:${fechaObj.getMinutes().toString().padStart(2, '0')}:${fechaObj.getSeconds().toString().padStart(2, '0')}`;
                        
                        return (
                        <div key={index} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <p className="text-sm font-medium text-gray-900">{evento.accion}</p>
                              <p className="text-xs text-gray-500 font-semibold">{fechaFormateada}</p>
                            </div>
                            <p className="text-xs text-gray-500 mb-1">Usuario: {evento.usuario}</p>
                            <p className="text-xs text-gray-400">
                              Hora exacta: <span className="font-mono font-semibold">{horaExacta}</span>
                            </p>
                          </div>
                        </div>
                      )});
                    }
                    return <p className="text-sm text-gray-500">No hay historial disponible</p>;
                  })()}
                </div>
              </div>
            </div>
          </div>
          </div>
        )}

        {/* Modal de Auditoría Avanzada */}
        {mostrarAuditoria && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-6xl shadow-xl max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Auditoría de Caja Chica</h3>
                <button
                  onClick={() => setMostrarAuditoria(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            
              <div className="mb-6">
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200 max-w-xs">
                  <div className="flex items-center">
                    <Database className="w-4 h-4 text-gray-600 mr-2" />
                    <span className="font-medium text-gray-700 text-sm">Total Movimientos</span>
                  </div>
                  <p className="text-xl font-semibold text-gray-900 mt-2">{gastosFiltrados.length}</p>
                </div>
              </div>

              {/* Línea de Tiempo de Eventos */}
              <div className="mb-6">
                <h4 className="text-base font-semibold text-gray-900 mb-4">Línea de Tiempo de Eventos</h4>
                {cargandoAuditoria ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
                    <span className="text-gray-600">Cargando eventos de auditoría...</span>
                  </div>
                ) : auditoriaReal.length > 0 ? (
                  <div className="space-y-4">
                    {auditoriaReal.map((evento, index) => (
                      <div key={evento.id} className="flex items-start space-x-4">
                        <div className="flex flex-col items-center">
                          <div className={`w-3 h-3 rounded-full ${
                            evento.resultado === 'OK' ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          {index < auditoriaReal.length - 1 && (
                            <div className="w-px h-8 bg-gray-300 mt-2"></div>
                          )}
                        </div>
                        <div className="flex-1 bg-gray-50 p-4 rounded-lg">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-gray-900">{evento.accion}</span>
                            <span className="text-xs text-gray-500 font-medium">
                              {formatDateTimeDDMMYYYY(evento.fecha)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 mb-1">Usuario: {evento.usuario}</p>
                          <p className="text-sm text-gray-500">{evento.detalle}</p>
                          <div className="mt-2 flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              evento.resultado === 'OK' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                              {evento.resultado || 'OK'}
                            </span>
                            <span className="text-xs text-gray-400">
                              {(() => {
                                try {
                                  const fechaEvento = new Date(evento.fecha);
                                  const horas = fechaEvento.getHours().toString().padStart(2, '0');
                                  const minutos = fechaEvento.getMinutes().toString().padStart(2, '0');
                                  const segundos = fechaEvento.getSeconds().toString().padStart(2, '0');
                                  return `${horas}:${minutos}:${segundos}`;
                                } catch {
                                  return '';
                                }
                              })()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-600 font-medium mb-1">No hay eventos de auditoría</p>
                    <p className="text-sm text-gray-500">Los eventos aparecerán aquí cuando se realicen acciones en el sistema</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Sistema de Toasts */}
        {toast.mostrar && (
          <div className="fixed top-4 right-4 z-50">
            <div className={`px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 ${
              toast.tipo === 'success' ? 'bg-green-100 text-green-800 border border-green-200' :
              toast.tipo === 'error' ? 'bg-red-100 text-red-800 border border-red-200' :
              toast.tipo === 'warning' ? 'bg-yellow-100 text-yellow-800 border border-yellow-200' :
              'bg-blue-100 text-blue-800 border border-blue-200'
            }`}>
              {toast.tipo === 'success' && <CheckCircle className="w-5 h-5" />}
              {toast.tipo === 'error' && <XCircle className="w-5 h-5" />}
              {toast.tipo === 'warning' && <AlertTriangle className="w-5 h-5" />}
              {toast.tipo === 'info' && <Info className="w-5 h-5" />}
              <span className="font-medium">{typeof toast.mensaje === 'string' ? toast.mensaje : String(toast.mensaje || '')}</span>
              <button
                onClick={() => setToast({ mostrar: false, mensaje: '', tipo: 'success' })}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Logs de Auditoría */}
        {mostrarAuditoria && hasPermission('view_auditoria') && (
          <div className="mt-6">
            <Card className="bg-white border border-gray-200 shadow-sm">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <History className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-gray-900">Logs de Auditoría</h2>
                      <p className="text-xs text-gray-600 mt-0.5">
                        Registro completo de acciones realizadas en el sistema
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <AuditLogViewer 
                  itemsPerPage={10}
                  autoRefresh={true}
                  refreshInterval={30000}
                />
              </div>
            </Card>
          </div>
        )}

      </div>

      {/* Modal de Detalle de Gasto desde Alertas */}
      <GastoDetailModal
        gasto={gastoSeleccionadoModal}
        isOpen={gastoModalAbierto}
        onClose={() => {
          setGastoModalAbierto(false);
          setGastoSeleccionadoModal(null);
        }}
      />
    </div>
  );
}


